import {
  ArticleIcon,
  BookOpenTextIcon,
  CaretLeftIcon,
  GlobeIcon,
  HeartIcon,
  LineSegmentsIcon,
  MegaphoneIcon,
  SlidersHorizontalIcon,
  SparkleIcon,
  SpinnerIcon,
  WaveformIcon,
} from '@phosphor-icons/react'
import { CodeFile, type EditorError, type KeyOverrideFunction } from 'mini-code'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AIAutoDJModal } from './AIAutoDJModal.tsx'
import { AIGenerateModal } from './AIGenerateModal.tsx'
import { api, type Track } from './api.ts'
import { useAuth } from './AuthContext.tsx'
import { AuthModal } from './AuthModal.tsx'
import { AutoCompileManager } from './AutoCompileManager.tsx'
import { BytecodeCompiler } from './bytecode-compiler.ts'
import type { BytecodeProgram } from './bytecode.ts'
import { CompiledOutput } from './CompiledOutput.tsx'
import { Crossfader } from './Crossfader.tsx'
import { DocsModal } from './DocsModal.tsx'
import { DownloadAudioModal } from './DownloadAudioModal.tsx'
import { dspscript } from './dsp-script.ts'
import { DualModeCodeUpdateManager } from './DualModeCodeUpdateManager.tsx'
import { adjustErrorLineNumbers, convertToEditorErrors } from './editor-errors.ts'
import { EditorControls } from './EditorControls.tsx'
import { EditorErrorBanner } from './EditorErrorBanner.tsx'
import { EditorPanel } from './EditorPanel.tsx'
import { exportAudioFromCode } from './export-audio.ts'
import { NavBar } from './NavBar.tsx'
import { OnboardingModal } from './OnboardingModal.tsx'
import { Link } from './Router.tsx'
import { SaveForm } from './SaveForm.tsx'
import { SetPasswordModal } from './SetPasswordModal.tsx'
import {
  type EditorTab,
  useAISettingsStore,
  useAutoDJSettingsStore,
  useChannelModeStore,
  useCompileModeStore,
  useCrossfadeStore,
  useDualModeCodeStore,
  useEditorControlsStore,
  useEditorErrorsStore,
  useEditorSettingsStore,
  useExportedAudioStore,
  useGlobalBpmStore,
  useLatencyHintStore,
  useNavBarHeightStore,
  usePlaybackStore,
  useSessionStore,
  useSidebarTabStore,
  useTabsStore,
} from './store.ts'
import { TabBar } from './TabBar.tsx'
import { TabUpdateManager } from './TabUpdateManager.tsx'
import { TracksList } from './TracksList.tsx'
import { Visualizers } from './Visualizers.tsx'

const compiler = new BytecodeCompiler()

function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: any
  return function(this: any, ...args: Parameters<T>) {
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(this, args), wait)
  }
}

const startupTime = Date.now()

// Don't subscribe to tabs array - it changes on every code edit
// Instead, we'll use getTabs() non-reactively and only subscribe to activeTabId

function App() {
  // console.log('render app')
  const { user } = useAuth()
  // Subscribe only to isPlaying, get setIsPlaying non-reactively
  const isPlaying = usePlaybackStore(state => state.isPlaying)
  const getPlaybackStore = usePlaybackStore.getState
  const setIsPlaying = getPlaybackStore().setIsPlaying
  const setPlayingTabIdInStore = getPlaybackStore().setPlayingTabId
  // Use selectors to minimize rerenders - only subscribe to what we need
  const activeTabId = useTabsStore(state => state.activeTabId)
  // Get store API non-reactively - these functions are stable references
  const getTabs = useTabsStore.getState
  // Memoize store methods to prevent recreation on every render
  const tabsStoreMethods = useMemo(() => {
    const store = getTabs()
    return {
      setActiveTabId: store.setActiveTabId,
      addTab: store.addTab,
      updateTab: store.updateTab,
      closeTab: store.closeTab,
      createNewTab: store.createNewTab,
    }
  }, [getTabs])
  const { setActiveTabId, addTab, updateTab, closeTab, createNewTab } = tabsStoreMethods

  // Get active tab metadata without subscribing to tabs array
  // We'll use getTabs() non-reactively inside useMemo to avoid rerenders on code changes
  // For isDirty, use a manual subscription that only fires when isDirty actually changes
  const [activeTabIsDirty, setActiveTabIsDirty] = useState(() => {
    const tabs = getTabs().tabs
    const tab = tabs.find(t => t.id === activeTabId)
    return tab?.isDirty ?? false
  })

  const prevIsDirtyRef = useRef(activeTabIsDirty)

  // Stable equality function for subscription
  const equalityFnRef = useRef({
    equalityFn: (a: boolean, b: boolean) => a === b,
  })

  // Stable selector function - doesn't depend on closure variables
  const selectIsDirty = useCallback((state: ReturnType<typeof useTabsStore.getState>) => {
    const tab = state.tabs.find(t => t.id === state.activeTabId)
    return tab?.isDirty ?? false
  }, [])

  // Stable callback for subscription - uses ref to avoid closure
  const handleIsDirtyChange = useCallback((isDirty: boolean) => {
    // Only update if value actually changed to prevent unnecessary rerenders
    if (prevIsDirtyRef.current !== isDirty) {
      prevIsDirtyRef.current = isDirty
      setActiveTabIsDirty(isDirty)
    }
  }, [])

  useEffect(() => {
    // Update when activeTabId changes
    const tabs = getTabs().tabs
    const tab = tabs.find(t => t.id === activeTabId)
    const initialIsDirty = tab?.isDirty ?? false
    prevIsDirtyRef.current = initialIsDirty
    setActiveTabIsDirty(initialIsDirty)

    // Subscribe with selector - only fires when isDirty for active tab changes
    const unsubscribe = useTabsStore.subscribe(
      selectIsDirty,
      handleIsDirtyChange,
      equalityFnRef.current,
    )

    return unsubscribe
  }, [activeTabId, selectIsDirty, handleIsDirtyChange])
  const activeTab = useMemo(() => {
    // Use getTabs() non-reactively - this won't cause rerenders when tabs array changes
    const tabs = getTabs().tabs
    const tab = tabs.find(t => t.id === activeTabId)
    if (!tab) return null
    return {
      id: tab.id,
      title: tab.title,
      artist: tab.artist,
      trackId: tab.trackId,
      editingTrackId: tab.editingTrackId,
    }
    // Only depend on activeTabId - not on tabs array or getTabs (which is stable)
    // This means activeTab will only update when activeTabId changes, not when code changes
  }, [activeTabId])
  // Subscribe only to channelMode, get setChannelMode non-reactively
  const channelMode = useChannelModeStore(state => state.channelMode)
  const getChannelModeStore = useChannelModeStore.getState
  // Memoize channel mode store method to prevent recreation
  const setChannelMode = useMemo(() => getChannelModeStore().setChannelMode, [getChannelModeStore])
  const navBarHeight = useNavBarHeightStore(state => state.height)
  // Get dual mode store non-reactively - access via getDualModeStore() to get fresh values
  const getDualModeStore = useDualModeCodeStore.getState
  // Create refs that return fresh values from store - memoized to prevent recreation
  const dualModeCodeARef = useMemo(() => ({
    get current() {
      return getDualModeStore().codeA
    },
  }), [getDualModeStore])
  const dualModeCodeBRef = useMemo(() => ({
    get current() {
      return getDualModeStore().codeB
    },
  }), [getDualModeStore])
  const dualModeTitleARef = useMemo(() => ({
    get current() {
      return getDualModeStore().titleA
    },
  }), [getDualModeStore])
  const dualModeArtistARef = useMemo(() => ({
    get current() {
      return getDualModeStore().artistA
    },
  }), [getDualModeStore])
  const dualModeTitleBRef = useMemo(() => ({
    get current() {
      return getDualModeStore().titleB
    },
  }), [getDualModeStore])
  const dualModeArtistBRef = useMemo(() => ({
    get current() {
      return getDualModeStore().artistB
    },
  }), [getDualModeStore])
  const setDualModeCodeARef = useMemo(() => ({
    get current() {
      return getDualModeStore().setDualModeCodeA
    },
  }), [getDualModeStore])
  const setDualModeCodeBRef = useMemo(() => ({
    get current() {
      return getDualModeStore().setDualModeCodeB
    },
  }), [getDualModeStore])
  // Convenience getters for direct access (read-only snapshots for render)
  const dualModeCodeA = dualModeCodeARef.current
  const dualModeCodeB = dualModeCodeBRef.current
  const dualModeTitleA = dualModeTitleARef.current
  const dualModeArtistA = dualModeArtistARef.current
  const dualModeTitleB = dualModeTitleBRef.current
  const dualModeArtistB = dualModeArtistBRef.current
  const setDualModeCodeA = setDualModeCodeARef.current
  const setDualModeCodeB = setDualModeCodeBRef.current
  const getCrossfadeStore = useCrossfadeStore.getState
  // Memoize crossfade store method to prevent recreation
  const setCrossfade = useMemo(() => getCrossfadeStore().setCrossfade, [getCrossfadeStore])
  const {
    setEditorInitiatedPlayback,
    setEditorPlayingTabId,
    removeEditorInitiatedPlayback,
    removeEditorPlayingTabId,
    setPublicTracksActiveTrackId,
    removePublicTracksActiveTrackId,
    setPublicTracksActiveRemixId,
    removePublicTracksActiveRemixId,
    setRemixTrack,
    removeRemixTrack,
    setSkipExampleOnce,
    removeSkipExampleOnce,
    setAutoplayOnLoad,
    removeAutoplayOnLoad,
    getEditorInitiatedPlayback,
    getEditorPlayingTabId,
    getPublicTracksActiveTrackId,
    getPublicTracksActiveRemixId,
    getRemixTrack,
    getSkipExampleOnce,
    getAutoplayOnLoad,
  } = useSessionStore()

  // Store CodeFile instances per tab
  const codeFilesMapRef = useRef<Map<string, { a: CodeFile; b: CodeFile }>>(new Map())

  // Separate CodeFile instances for dual mode (not tied to tabs)
  const dualModeCodeFilesRef = useRef<{ a: CodeFile; b: CodeFile } | null>(null)
  const dualModeInitializedRef = useRef(false)

  // Get or create CodeFile instances for a tab
  const getCodeFiles = useCallback((tabId: string) => {
    if (!codeFilesMapRef.current.has(tabId)) {
      codeFilesMapRef.current.set(tabId, {
        a: new CodeFile(''),
        b: new CodeFile(''),
      })
    }
    return codeFilesMapRef.current.get(tabId)!
  }, [])

  // Get or create dual mode CodeFile instances and initialize from persisted state
  const getDualModeCodeFiles = useCallback(() => {
    if (!dualModeCodeFilesRef.current) {
      dualModeCodeFilesRef.current = {
        a: new CodeFile(''),
        b: new CodeFile(''),
      }

      // Initialize from persisted state immediately upon creation (only if hydrated)
      const store = getDualModeStore()
      const hasPersistedData = store.codeFileStateA || store.codeFileStateB || store.codeA || store.codeB

      if (store._hasHydrated && hasPersistedData) {
        if (store.codeFileStateA) {
          dualModeCodeFilesRef.current.a.setState(store.codeFileStateA)
        }
        else if (store.codeA) {
          dualModeCodeFilesRef.current.a.value = store.codeA
        }

        if (store.codeFileStateB) {
          dualModeCodeFilesRef.current.b.setState(store.codeFileStateB)
        }
        else if (store.codeB) {
          dualModeCodeFilesRef.current.b.value = store.codeB
        }

        dualModeInitializedRef.current = true
      }
    }
    return dualModeCodeFilesRef.current
  }, [])

  // Current tab's CodeFile instances - use refs to avoid re-renders on subscription changes
  const currentCodeFilesRef = useRef<{ a: CodeFile; b: CodeFile }>(getCodeFiles(activeTabId))
  // Track active tab ID to force re-render when switching tabs
  const [activeTabCodeFilesKey, setActiveTabCodeFilesKey] = useState(activeTabId)

  const [currentTrackId, setCurrentTrackId] = useState<string | undefined>(undefined)
  const [status, setStatus] = useState('Ready')
  // Subscribe only to values we read in render, get setters non-reactively
  const globalBpm = useGlobalBpmStore(state => state.globalBpm)
  const getGlobalBpmStore = useGlobalBpmStore.getState
  // Memoize global BPM store method to prevent recreation
  const setGlobalBpm = useMemo(() => getGlobalBpmStore().setGlobalBpm, [getGlobalBpmStore])
  const latencyHint = useLatencyHintStore(state => state.latencyHint)
  const getLatencyHintStore = useLatencyHintStore.getState
  // Memoize latency hint store method to prevent recreation
  const setLatencyHint = useMemo(() => getLatencyHintStore().setLatencyHint, [getLatencyHintStore])
  const [sampleRate, setSampleRate] = useState('-')
  const [instrCount, setInstrCount] = useState('-')
  const [error, setError] = useState('')
  // Get error setters non-reactively to avoid re-renders on error updates
  const getEditorErrorsStore = useEditorErrorsStore.getState
  // Memoize error store methods to prevent recreation
  const editorErrorsStoreMethods = useMemo(() => {
    const store = getEditorErrorsStore()
    return {
      setErrorsA: store.setErrorsA,
      setErrorsB: store.setErrorsB,
    }
  }, [getEditorErrorsStore])
  const { setErrorsA, setErrorsB } = editorErrorsStoreMethods
  const [syncBars, setSyncBars] = useState<number>(0)
  const trackLoadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const compileMode = useCompileModeStore(state => state.compileMode)
  // Track which tracks have compilation errors (stores error message or null)
  const [trackErrors, setTrackErrors] = useState<Map<string, string | null>>(new Map())
  // Subscribe only to values we read in render, get setters non-reactively
  const sidebarTab = useSidebarTabStore(state => state.sidebarTab)
  const sidebarTabHasHydrated = useSidebarTabStore(state => state._hasHydrated)
  const getSidebarTabStore = useSidebarTabStore.getState
  // Memoize sidebar tab store method to prevent recreation
  const setSidebarTab = useMemo(() => getSidebarTabStore().setSidebarTab, [getSidebarTabStore])
  const [tracks, setTracks] = useState<Track[]>([])
  const [likedTracks, setLikedTracks] = useState<Track[]>([])

  // Check if a track has compilation errors and return error message
  const checkTrackError = useCallback((track: Track): string | null => {
    try {
      compiler.compile(track.code)
      return null
    }
    catch (err: any) {
      return err.message || 'Compilation error'
    }
  }, [])

  // Update track error state
  const updateTrackError = useCallback((trackId: string, errorMessage: string | null) => {
    setTrackErrors(prev => {
      const next = new Map(prev)
      if (errorMessage) {
        next.set(trackId, errorMessage)
      }
      else {
        next.delete(trackId)
      }
      return next
    })
  }, [])

  // Check all tracks for errors when they're loaded
  // My Tracks and Liked Tracks are lazily compiled on play
  const [loadingTracks, setLoadingTracks] = useState(false)
  const [loadingLiked, setLoadingLiked] = useState(false)
  const [loadingMoreTracks, setLoadingMoreTracks] = useState(false)
  const [loadingMoreLiked, setLoadingMoreLiked] = useState(false)
  const [tracksCursor, setTracksCursor] = useState<string | undefined>(undefined)
  const [likedTracksCursor, setLikedTracksCursor] = useState<string | undefined>(undefined)
  const [hasMoreTracks, setHasMoreTracks] = useState(false)
  const [hasMoreLikedTracks, setHasMoreLikedTracks] = useState(false)
  const tracksCursorRef = useRef<string | undefined>(undefined)
  const likedTracksCursorRef = useRef<string | undefined>(undefined)
  const isLoadingMoreTracksRef = useRef(false)
  const isLoadingMoreLikedRef = useRef(false)
  const [saveTitle, setSaveTitle] = useState('')
  const [savePublic, setSavePublic] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveAsRemix, setSaveAsRemix] = useState(false)
  const [saveMenuOpen, setSaveMenuOpen] = useState(false)
  const [generatingTitle, setGeneratingTitle] = useState(false)
  const [visualizerMode, setVisualizerMode] = useState<0 | 1 | 2 | 3>(0)
  const getEditorControlsStore = useEditorControlsStore.getState
  // Memoize editor controls store method to prevent recreation
  const setLoading = useMemo(() => getEditorControlsStore().setLoading, [getEditorControlsStore])
  const isFullscreen = useEditorControlsStore(state => state.isFullscreen)
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null)
  const [likedTrackIds, setLikedTrackIds] = useState<Set<string>>(new Set())

  // Get track for EditorControls - use currentTrack if available, otherwise construct from activeTab or dual mode
  const trackForEditorControls = useMemo(() => {
    if (currentTrack) return currentTrack

    const now = Date.now()

    if (channelMode === 'dual') {
      const dualFiles = getDualModeCodeFiles()
      const dualStore = getDualModeStore()
      return {
        id: 'dual-mode-track',
        title: dualStore.titleA || 'Untitled',
        userName: dualStore.artistA || 'Unknown',
        code: dualFiles.a.value,
        userId: user?.id || '',
        isPublic: false,
        likesCount: 0,
        commentsCount: 0,
        createdAt: now,
        updatedAt: now,
      } as Track
    }

    if (!activeTab) return null

    const code = getCodeFiles(activeTabId).a.value

    return {
      id: activeTab.trackId || activeTab.id,
      title: activeTab.title,
      userName: activeTab.artist,
      code,
      userId: user?.id || '',
      isPublic: false,
      likesCount: 0,
      commentsCount: 0,
      createdAt: now,
      updatedAt: now,
    } as Track
  }, [currentTrack, activeTab, activeTabId, channelMode, user?.id])
  const [editingTrackId, setEditingTrackId] = useState<string | null>(null)
  const [trackParents, setTrackParents] = useState<Record<string, Track>>({})
  const [showDocsModal, setShowDocsModal] = useState(false)
  const [activeSidebarTrackId, setActiveSidebarTrackId] = useState<string | null>(null)
  const [sidebarPlayingTrackId, setSidebarPlayingTrackId] = useState<string | null>(null)
  const [playingTabId, setPlayingTabId] = useState<string | null>(null)
  const [focusedDualEditor, setFocusedDualEditor] = useState<'A' | 'B' | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [showAIModal, setShowAIModal] = useState(false)
  const [showAutoDJModal, setShowAutoDJModal] = useState(false)
  const [isAutoDJRunning, setIsAutoDJRunning] = useState(false)
  const autoDJStopRef = useRef(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [exportBpm, setExportBpm] = useState(120)
  const { exportedAudioUrl, exportedFilename, setExportedAudio, clearExportedAudio } = useExportedAudioStore()
  const [showOnboardingModal, setShowOnboardingModal] = useState(false)

  // Memoize URL params to prevent recreation on every render
  const hasNoAccount = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    return params.has('noaccount')
  }, [])

  // Handle URL params on initial mount (only once, after hydration)
  const urlParamsHandledRef = useRef(false)
  useEffect(() => {
    if (urlParamsHandledRef.current || !sidebarTabHasHydrated) return
    urlParamsHandledRef.current = true

    const params = new URLSearchParams(window.location.search)
    if (params.has('aidj')) {
      setChannelMode('dual')
      setShowAutoDJModal(true)
      const autoDJStore = useAutoDJSettingsStore.getState()
      const prompt = params.get('prompt')
      if (prompt) {
        autoDJStore.setMessage(decodeURIComponent(prompt))
      }
      const swap = params.get('swap')
      if (swap) {
        const swapValue = Number(swap)
        if (!isNaN(swapValue) && swapValue > 0) {
          autoDJStore.setSecondsToSwap(swapValue)
        }
      }
      const mix = params.get('mix')
      if (mix) {
        const mixValue = Number(mix)
        if (!isNaN(mixValue) && mixValue > 0) {
          autoDJStore.setMixDuration(mixValue)
        }
      }
      const aiStore = useAISettingsStore.getState()
      const temperature = params.get('temperature')
      if (temperature) {
        const temperatureValue = Number(temperature)
        if (!isNaN(temperatureValue) && temperatureValue > 0) {
          aiStore.setTemperature(temperatureValue)
        }
      }
      const topP = params.get('top_p')
      if (topP) {
        const topPValue = Number(topP)
        if (!isNaN(topPValue) && topPValue > 0) {
          aiStore.setTopP(topPValue)
        }
      }
    }
    // Only override persisted sidebarTab if URL param is present
    if (params.has('noaccount')) {
      setSidebarTab('compiled')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarTabHasHydrated])

  useEffect(() => {
    if (window.location.pathname === '/editor') {
      const hasSeenOnboarding = localStorage.getItem('hasSeenEditorOnboarding')
      if (!hasSeenOnboarding) {
        setShowOnboardingModal(true)
      }
    }
  }, [])

  const handleOnboardingClose = () => {
    localStorage.setItem('hasSeenEditorOnboarding', 'true')
    setShowOnboardingModal(false)
  }

  useEffect(() => {
    if (channelMode !== 'dual') return

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as Node
      if (editorAContainerRef.current?.contains(target)) {
        setFocusedDualEditor('A')
      }
      else if (editorBContainerRef.current?.contains(target)) {
        setFocusedDualEditor('B')
      }
      else {
        setFocusedDualEditor(null)
      }
    }

    document.addEventListener('focusin', handleFocusIn)

    return () => {
      document.removeEventListener('focusin', handleFocusIn)
    }
  }, [channelMode])

  const lastCompiledCodeA = useRef<string>('')
  const lastCompiledCodeB = useRef<string>('')
  const lastSeenCodeA = useRef<string>('')
  const lastSeenCodeB = useRef<string>('')
  const exampleCodeRef = useRef('')
  // Store CodeFile instances in refs - update only when switching tabs/modes
  // This ensures CodeFile subscription changes (like caret moves) don't trigger App re-renders
  const codeFileARef = useRef<CodeFile>(getCodeFiles(activeTabId).a)
  const codeFileBRef = useRef<CodeFile>(getCodeFiles(activeTabId).b)
  const prevChannelModeRef = useRef(channelMode)
  const prevActiveTabIdRefForCodeFiles = useRef(activeTabId)

  const handleCompileRef = useRef<typeof handleCompile | null>(null)
  const editorErrorsARef = useRef<EditorError[]>([])
  const editorErrorsBRef = useRef<EditorError[]>([])
  const errorRef = useRef('')
  const switchingFromDualRef = useRef(false)
  const prevUserRef = useRef<typeof user | null>(null)
  const prevUserIdRef = useRef<string | null>(null)
  const loadingLikedRef = useRef(false)
  const isTogglingLikeRef = useRef(false)
  const tabBarRef = useRef<HTMLDivElement | null>(null)
  const hasScrolledInitialTabRef = useRef(false)
  const prevChannelModeForScrollRef = useRef(channelMode)
  const editorAContainerRef = useRef<HTMLDivElement | null>(null)
  const editorBContainerRef = useRef<HTMLDivElement | null>(null)
  const prevActiveTabIdRef = useRef<string | null>(null)
  const saveTitleManuallyEditedRef = useRef(false)
  const userStateHandledRef = useRef(false)

  // Update CodeFile refs synchronously when mode changes to prevent showing wrong content
  if (channelMode === 'dual') {
    const dualFiles = getDualModeCodeFiles()
    if (codeFileARef.current !== dualFiles.a) {
      codeFileARef.current = dualFiles.a
    }
    if (codeFileBRef.current !== dualFiles.b) {
      codeFileBRef.current = dualFiles.b
    }
    if (prevChannelModeRef.current !== channelMode) {
      prevChannelModeRef.current = channelMode
    }
  }
  else {
    // Single mode - update when tab or mode changes
    if (
      prevActiveTabIdRefForCodeFiles.current !== activeTabId
      || prevChannelModeRef.current !== channelMode
    ) {
      const newCodeFiles = getCodeFiles(activeTabId)
      codeFileARef.current = newCodeFiles.a
      codeFileBRef.current = newCodeFiles.b
      prevActiveTabIdRefForCodeFiles.current = activeTabId
      prevChannelModeRef.current = channelMode
      setActiveTabCodeFilesKey(activeTabId)
    }
  }

  // Get CodeFile instances - memoize to ensure stable references
  // Using refs ensures CodeFile subscription changes (like caret moves) don't trigger re-renders
  const codeFileA = useMemo(() => codeFileARef.current, [activeTabCodeFilesKey, channelMode])
  const codeFileB = useMemo(() => codeFileBRef.current, [activeTabCodeFilesKey, channelMode])

  // Derive effective saveTitle - use state value if manually edited, otherwise sync with active tab
  const effectiveSaveTitle = useMemo(() => {
    if (saveTitleManuallyEditedRef.current) {
      return saveTitle
    }
    return activeTab && activeTab.title !== 'Untitled' ? activeTab.title : ''
  }, [saveTitle, activeTab])

  // Memoize save button disabled state to prevent rerenders
  // Use refs to cache the result and only update when relevant values actually change
  const prevSaveButtonStateRef = useRef<{
    isDisabled: boolean
    hasChanges: boolean
    isOwn: boolean
  }>({
    isDisabled: true,
    hasChanges: false,
    isOwn: false,
  })
  const prevSaveButtonStateKeyRef = useRef<string>('')

  // Create a key from the values that affect the save button state
  // Use fresh tab from store to ensure we get the latest title
  const freshActiveTab = getTabs().tabs.find(t => t.id === activeTabId)
  const saveButtonStateKey =
    `${activeTabId}|${freshActiveTab?.id}|${effectiveSaveTitle.trim()}|${freshActiveTab?.title.trim()}|${currentTrack?.id}|${user?.id}|${currentTrackId}|${saving}|${activeTabIsDirty}`

  // Only recalculate if the key changed
  let saveButtonState = prevSaveButtonStateRef.current
  if (saveButtonStateKey !== prevSaveButtonStateKeyRef.current) {
    const fullActiveTab = getTabs().tabs.find(t => t.id === activeTabId)
    const effectiveTitle = effectiveSaveTitle.trim()
    const tabTitle = fullActiveTab?.title.trim() || ''
    const titleChanged = !!(fullActiveTab && effectiveTitle !== tabTitle
      && !(effectiveTitle === '' && (tabTitle === '' || tabTitle === 'Untitled')))
    const hasChanges = activeTabIsDirty || titleChanged
    const isOwn = !!(currentTrack && user && currentTrack.userId === user.id)
    const remixTrackData = getRemixTrack()
    const isLoadedAsRemix = remixTrackData?.trackId === currentTrackId
    const trackExistsInSystem = !!(
      currentTrackId
      && (tracks.find(t => t.id === currentTrackId)
        || likedTracks.find(t => t.id === currentTrackId)
        || currentTrack)
    )
    const isNotOwnAndNoChanges = !isOwn && !hasChanges && !!currentTrack
    const shouldDisableSave = isNotOwnAndNoChanges && (isLoadedAsRemix || trackExistsInSystem)
    const isDisabled = !!(saving || !hasChanges || shouldDisableSave)
    saveButtonState = { isDisabled, hasChanges, isOwn }
    prevSaveButtonStateRef.current = saveButtonState
    prevSaveButtonStateKeyRef.current = saveButtonStateKey
  }

  const prevSourceCodeA = useRef(codeFileA.value)
  const prevSourceCodeB = useRef(codeFileB.value)
  const prevCurrentTrackIdRef = useRef<string | undefined>(undefined)
  const fetchingTrackRef = useRef<string | null>(null)

  // Request persistent storage for localStorage
  useEffect(() => {
    if ('storage' in navigator && 'persist' in navigator.storage) {
      navigator.storage.persist()
    }
  }, [])

  // Clean up CodeFile instances for closed tabs
  useEffect(() => {
    const tabs = getTabs().tabs
    const tabIds = new Set(tabs.map(t => t.id))
    const codeFileIds = Array.from(codeFilesMapRef.current.keys())

    // Remove CodeFile instances for tabs that no longer exist
    for (const id of codeFileIds) {
      if (!tabIds.has(id)) {
        codeFilesMapRef.current.delete(id)
      }
    }
  }, [activeTabId]) // Only rerun when activeTabId changes, not when tabs array changes

  // Switch CodeFile instances when active tab changes (only in single mode)
  useEffect(() => {
    if (channelMode === 'dual') return

    const newCodeFiles = getCodeFiles(activeTabId)
    currentCodeFilesRef.current = newCodeFiles
    setActiveTabCodeFilesKey(activeTabId)

    // Get full tab data (including code) from store - non-reactive
    const fullActiveTab = getTabs().tabs.find(t => t.id === activeTabId)
    if (!fullActiveTab) return

    // Initialize CodeFile instances if they don't have content yet
    // Only set if different to avoid triggering subscriptions
    if (newCodeFiles.a.value !== fullActiveTab.codeA) {
      // Update ref FIRST to prevent marking tab as dirty
      prevSourceCodeA.current = fullActiveTab.codeA
      // Update last compiled code to prevent auto-compile when switching tabs
      lastCompiledCodeA.current = fullActiveTab.codeA
      lastSeenCodeA.current = fullActiveTab.codeA

      if (fullActiveTab.codeFileStateA) {
        newCodeFiles.a.setState(fullActiveTab.codeFileStateA)
        // Clear errors when restoring code from state (undo/redo, tab switch)
        setError('')
        setErrorsA([])
        setErrorsB([])
      }
      else {
        newCodeFiles.a.value = fullActiveTab.codeA
        // Clear errors when restoring code
        // Track compiled successfully - clear editor errors
        setError('')
        setErrorsA([])
        setErrorsB([])
      }
    }
    else {
      prevSourceCodeA.current = newCodeFiles.a.value
      lastCompiledCodeA.current = newCodeFiles.a.value
      lastSeenCodeA.current = newCodeFiles.a.value
    }

    if (newCodeFiles.b.value !== fullActiveTab.codeB) {
      // Update ref FIRST to prevent marking tab as dirty
      prevSourceCodeB.current = fullActiveTab.codeB
      // Update last compiled code to prevent auto-compile when switching tabs
      lastCompiledCodeB.current = fullActiveTab.codeB
      lastSeenCodeB.current = fullActiveTab.codeB

      if (fullActiveTab.codeFileStateB) {
        newCodeFiles.b.setState(fullActiveTab.codeFileStateB)
        // Clear errors when restoring code from state (undo/redo, tab switch)
        // Track compiled successfully - clear editor errors
        setError('')
        setErrorsA([])
        setErrorsB([])
      }
      else {
        newCodeFiles.b.value = fullActiveTab.codeB
        // Clear errors when restoring code
        // Track compiled successfully - clear editor errors
        setError('')
        setErrorsA([])
        setErrorsB([])
      }
    }
    else {
      prevSourceCodeB.current = newCodeFiles.b.value
      lastCompiledCodeB.current = newCodeFiles.b.value
      lastSeenCodeB.current = newCodeFiles.b.value
    }

    // Only update saveTitle when switching to a different tab, not when tab data changes
    if (prevActiveTabIdRef.current !== activeTabId) {
      setSaveTitle(fullActiveTab.title !== 'Untitled' ? fullActiveTab.title : '')
      saveTitleManuallyEditedRef.current = false
      prevActiveTabIdRef.current = activeTabId
    }
    setEditingTrackId(fullActiveTab.editingTrackId || null)
    setCurrentTrackId(fullActiveTab.trackId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId, activeTab, getCodeFiles, channelMode, getTabs])

  // Tab updates are handled by TabUpdateManager component

  // Subscribe to _hasHydrated to trigger hydration check
  const dualModeHasHydrated = useDualModeCodeStore(state => state._hasHydrated)

  // Restore persisted dual mode code if CodeFiles were created before persisted data was available
  useEffect(() => {
    if (channelMode !== 'dual' || !dualModeCodeFilesRef.current) return
    if (dualModeInitializedRef.current) return
    if (!dualModeHasHydrated) return

    const dualFiles = dualModeCodeFilesRef.current
    const store = getDualModeStore()
    const hasPersistedData = store.codeA || store.codeB || store.codeFileStateA || store.codeFileStateB

    if (!hasPersistedData) return

    // Restore from persisted state
    // IMPORTANT: Set prevSourceCode BEFORE updating CodeFile to prevent subscription from saving
    if (store.codeFileStateA) {
      prevSourceCodeA.current = store.codeFileStateA.value || ''
      dualFiles.a.setState(store.codeFileStateA)
      // Clear errors when restoring code from state
      setError('')
      setErrorsA([])
      setErrorsB([])
    }
    else if (store.codeA) {
      prevSourceCodeA.current = store.codeA
      dualFiles.a.value = store.codeA
      // Clear errors when restoring code
      setError('')
      setErrorsA([])
      setErrorsB([])
    }

    if (store.codeFileStateB) {
      prevSourceCodeB.current = store.codeFileStateB.value || ''
      dualFiles.b.setState(store.codeFileStateB)
      // Clear errors when restoring code from state
      setError('')
      setErrorsA([])
      setErrorsB([])
    }
    else if (store.codeB) {
      prevSourceCodeB.current = store.codeB
      dualFiles.b.value = store.codeB
      // Clear errors when restoring code
      setError('')
      setErrorsA([])
      setErrorsB([])
    }

    dualModeInitializedRef.current = true
  }, [channelMode, dualModeHasHydrated, getDualModeStore])

  // Dual mode code updates are handled by DualModeCodeUpdateManager component

  const handleNewTab = useCallback(async () => {
    try {
      let code = exampleCodeRef.current
      if (!code) {
        // Lazy-load the initial example if not yet loaded
        const response = await fetch('./spec/spec-simple.txt?t=' + startupTime)
        code = await response.text()
        exampleCodeRef.current = code
      }

      const newTab: EditorTab = {
        id: `tab-${Date.now()}`,
        title: 'Untitled',
        artist: '',
        codeA: code,
        codeB: code,
        isDirty: false,
      }
      const tabsStore = getTabs()
      tabsStore.addTab(newTab)
      tabsStore.setActiveTabId(newTab.id)

      // Scroll the new tab into view
      requestAnimationFrame(() => {
        scrollTabIntoView(newTab.id)
      })

      // Ensure editor reflects the new tab content immediately without marking dirty
      // Update refs BEFORE setting values to prevent triggering saves
      prevSourceCodeA.current = code
      prevSourceCodeB.current = code
      const codeFiles = getCodeFiles(newTab.id)
      codeFiles.a.value = code
      codeFiles.b.value = code

      // Compile the new tab code without playing
      setTimeout(() => {
        if (handleCompileRef.current) {
          handleCompileRef.current(code, code, false)
        }
      }, 100)
    }
    catch (err) {
      console.error('Failed to load initial code for new tab:', err)
      getTabs().createNewTab()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCloseTab = useCallback((tabId: string) => {
    const tabs = getTabs().tabs
    const tab = tabs.find(t => t.id === tabId)
    if (tab?.isDirty) {
      const confirmed = confirm('This tab has unsaved changes. Are you sure you want to close it?')
      if (!confirmed) return
    }
    getTabs().closeTab(tabId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const scrollTabIntoView = (tabId: string) => {
    if (!tabBarRef.current) return
    const tabElement = tabBarRef.current.querySelector(`[data-tab-id="${tabId}"]`) as HTMLElement
    if (tabElement) {
      requestAnimationFrame(() => {
        const container = tabBarRef.current
        if (!container) return
        // Estimate the width of the "+ (New Tab)" button, which overlays the right side of the tab bar.
        // Adjust this if you change the button's style/size in TabBar.
        const PLUS_BUTTON_WIDTH = 114 // px; tweak if needed

        const tabLeft = tabElement.offsetLeft
        const tabRight = tabElement.offsetLeft + tabElement.offsetWidth
        const containerLeft = container.scrollLeft
        const containerRight = container.scrollLeft + container.clientWidth - PLUS_BUTTON_WIDTH

        if (tabLeft < containerLeft) {
          container.scrollLeft = tabLeft
        }
        else if (tabRight > containerRight) {
          container.scrollLeft = tabRight - container.clientWidth + PLUS_BUTTON_WIDTH
        }
      })
    }
  }

  const handleSwitchTab = useCallback((tabId: string) => {
    // Preserve scroll position when switching tabs
    const scrollLeft = tabBarRef.current?.scrollLeft ?? 0
    getTabs().setActiveTabId(tabId)
    // Restore scroll position after state update
    requestAnimationFrame(() => {
      if (tabBarRef.current) {
        tabBarRef.current.scrollLeft = scrollLeft
        scrollTabIntoView(tabId)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (currentTrackId) {
      const track = tracks.find(t => t.id === currentTrackId) || likedTracks.find(t => t.id === currentTrackId)
      // Only update currentTrack if we found it in tracks/likedTracks
      // Otherwise, it might already be set from PublicTracks data
      if (track) {
        setCurrentTrack(track)
        setSaveAsRemix(true)
        setSavePublic(track.isPublic)
      }
      else if (prevCurrentTrackIdRef.current !== currentTrackId) {
        // If currentTrackId changed and we can't find the track locally,
        // try to preserve existing currentTrack if it matches, otherwise fetch it
        setCurrentTrack(prev => (prev?.id === currentTrackId ? prev : null))
        setSaveAsRemix(true)

        // Fetch the track if we don't have it and we're not already fetching it
        if (fetchingTrackRef.current !== currentTrackId) {
          fetchingTrackRef.current = currentTrackId
          ;(async () => {
            try {
              const response = await api.tracks.getTrack(currentTrackId)
              if (response.track && fetchingTrackRef.current === currentTrackId) {
                setCurrentTrack(response.track)
                setSaveAsRemix(true)
                setSavePublic(response.track.isPublic)
              }
            }
            catch (err) {
              console.error('Failed to load track:', err)
              if (fetchingTrackRef.current === currentTrackId) {
                setCurrentTrack(null)
              }
            }
            finally {
              if (fetchingTrackRef.current === currentTrackId) {
                fetchingTrackRef.current = null
              }
            }
          })()
        }
      }
      else {
        // currentTrackId hasn't changed, preserve currentTrack if it matches
        setCurrentTrack(prev => {
          if (prev?.id === currentTrackId) {
            // Sync savePublic with the preserved track
            setSavePublic(prev.isPublic)
            return prev
          }
          return null
        })
        setSaveAsRemix(true)
      }
    }
    else {
      setCurrentTrack(null)
      setSaveAsRemix(false)
    }
    prevCurrentTrackIdRef.current = currentTrackId
  }, [currentTrackId, tracks, likedTracks])

  useEffect(() => {
    if (user) {
      loadTracks()
      loadLikedTracks()
    }
  }, [user])

  useEffect(() => {
    if (user && sidebarTab === 'liked' && !loadingLikedRef.current && likedTracks.length === 0) {
      loadLikedTracks()
    }
  }, [sidebarTab, user, likedTracks.length])

  // Ensure likedTrackIds is synced with likedTracks, respecting isLiked property
  // Only sync tracks that are in likedTracks array - don't interfere with other toggles
  // Skip syncing if we're currently toggling a like to avoid race conditions
  useEffect(() => {
    if (likedTracks.length > 0 && !isTogglingLikeRef.current) {
      setLikedTrackIds(prev => {
        const newSet = new Set(prev)
        let changed = false

        for (const track of likedTracks) {
          // If track is explicitly marked as not liked, remove it from likedTrackIds
          // Otherwise (true or undefined), it should be in likedTrackIds since it's in likedTracks
          const shouldBeLiked = track.isLiked !== false
          const isInSet = newSet.has(track.id)

          if (shouldBeLiked && !isInSet) {
            newSet.add(track.id)
            changed = true
          }
          else if (!shouldBeLiked && isInSet) {
            newSet.delete(track.id)
            changed = true
          }
        }

        // Don't remove IDs for tracks that aren't in likedTracks - let other components manage those
        return changed ? newSet : prev
      })
    }
  }, [likedTracks])

  useEffect(() => {
    // Only run after hydration to avoid overriding persisted value
    if (!sidebarTabHasHydrated) return

    const currentUserId = user?.id ?? null
    const prevUserId = prevUserIdRef.current

    // Early return if user ID hasn't changed - preserve persisted value
    // This prevents the effect from running when user object reference changes but ID is the same
    if (prevUserId === currentUserId) {
      // Still update the ref to track the current user object
      prevUserRef.current = user
      return
    }

    // User ID actually changed - handle the transition
    const currentTab = useSidebarTabStore.getState().sidebarTab

    // User logged out - switch to 'compiled' if not already there
    if (prevUserId && !currentUserId) {
      if (currentTab !== 'compiled') {
        setSidebarTab('compiled')
      }
      userStateHandledRef.current = false
      prevUserRef.current = null
      prevUserIdRef.current = null
    }
    else {
      // User logged in or switched - preserve persisted tab preference, never override
      // The persisted value should be respected, don't change it
      if (!userStateHandledRef.current) {
        userStateHandledRef.current = true
      }
      prevUserRef.current = user
      prevUserIdRef.current = currentUserId
    }
  }, [user?.id, sidebarTabHasHydrated])

  useEffect(() => {
    if (!isPlaying) {
      setActiveSidebarTrackId(null)
      setPlayingTabId(null)
      setSidebarPlayingTrackId(null)
    }
  }, [isPlaying])

  const loadTracks = async (append = false) => {
    if (!user) return
    if (append && isLoadingMoreTracksRef.current) return

    try {
      if (append) {
        isLoadingMoreTracksRef.current = true
        setLoadingMoreTracks(true)
      }
      else {
        setLoadingTracks(true)
        setTracksCursor(undefined)
        tracksCursorRef.current = undefined
      }

      const response = await api.tracks.getUserTracks(10, append ? tracksCursorRef.current : undefined)

      // Clear errors for tracks being refreshed so they can be clicked again
      setTrackErrors(prev => {
        const next = new Map(prev)
        response.tracks.forEach(track => {
          next.delete(track.id)
        })
        return next
      })

      if (append) {
        setTracks(prev => [...prev, ...response.tracks])
      }
      else {
        setTracks(response.tracks)
      }

      // Extract like statuses and parent tracks from enriched data
      const likedIds = new Set<string>()
      const parentsMap: Record<string, Track> = {}
      for (const track of response.tracks) {
        // Extract like status from enriched data
        if (track.isLiked) {
          likedIds.add(track.id)
        }

        // Extract parent track from enriched data
        if (track.parentTrack) {
          parentsMap[track.id] = track.parentTrack
        }
      }
      setLikedTrackIds(prev => new Set([...prev, ...likedIds]))
      setTrackParents(prev => ({ ...prev, ...parentsMap }))

      setTracksCursor(response.cursor)
      tracksCursorRef.current = response.cursor
      setHasMoreTracks(!!response.cursor)
    }
    catch (err: any) {
      console.error('Failed to load tracks:', err)
    }
    finally {
      if (append) {
        isLoadingMoreTracksRef.current = false
        setLoadingMoreTracks(false)
      }
      else {
        setLoadingTracks(false)
      }
    }
  }

  const loadLikedTracks = async (append = false) => {
    if (!user) return
    if (append && isLoadingMoreLikedRef.current) return
    if (!append && loadingLikedRef.current) return

    try {
      if (append) {
        isLoadingMoreLikedRef.current = true
        setLoadingMoreLiked(true)
      }
      else {
        loadingLikedRef.current = true
        setLoadingLiked(true)
        setLikedTracksCursor(undefined)
        likedTracksCursorRef.current = undefined
      }

      const response = await api.tracks.getLikedTracks(10, append ? likedTracksCursorRef.current : undefined)

      // Clear errors for tracks being refreshed so they can be clicked again
      setTrackErrors(prev => {
        const next = new Map(prev)
        response.tracks.forEach(track => {
          next.delete(track.id)
        })
        return next
      })

      // Ensure all tracks are marked as liked
      const tracksWithLikeStatus = response.tracks.map(t => ({ ...t, isLiked: true }))
      if (append) {
        setLikedTracks(prev => [...prev, ...tracksWithLikeStatus])
      }
      else {
        setLikedTracks(tracksWithLikeStatus)
      }

      // All liked tracks are liked by definition
      const likedIds = new Set(response.tracks.map(t => t.id))
      setLikedTrackIds(prev => new Set([...prev, ...likedIds]))

      // Load parent tracks for remixes
      const parentsMap: Record<string, Track> = { ...trackParents }
      for (const track of response.tracks) {
        if (track.remixOf && !parentsMap[track.id]) {
          try {
            const parentResponse = await api.tracks.getTrack(track.remixOf)
            parentsMap[track.id] = parentResponse.track
          }
          catch {
            // Ignore errors loading parent
          }
        }
      }
      setTrackParents(prev => ({ ...prev, ...parentsMap }))

      setLikedTracksCursor(response.cursor)
      likedTracksCursorRef.current = response.cursor
      setHasMoreLikedTracks(!!response.cursor)
    }
    catch (err: any) {
      console.error('Failed to load liked tracks:', err)
    }
    finally {
      if (append) {
        isLoadingMoreLikedRef.current = false
        setLoadingMoreLiked(false)
      }
      else {
        setLoadingLiked(false)
        loadingLikedRef.current = false
      }
    }
  }

  const handleLoadMoreTracks = useCallback(() => {
    if (!isLoadingMoreTracksRef.current && hasMoreTracks) {
      loadTracks(true)
    }
  }, [hasMoreTracks])

  const handleLoadMoreLiked = useCallback(() => {
    if (!isLoadingMoreLikedRef.current && hasMoreLikedTracks) {
      loadLikedTracks(true)
    }
  }, [hasMoreLikedTracks])

  const handleSaveTrack = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedTitle = saveTitle.trim()
    if (!trimmedTitle) {
      alert('Please enter a title for your loop')
      return
    }

    try {
      setSaving(true)
      setError('')

      if (editingTrackId) {
        // Update existing track
        await api.tracks.update(editingTrackId, {
          title: trimmedTitle,
          code: channelMode === 'single' ? codeFileA.value : codeFileA.value,
          isPublic: savePublic,
        })
        updateTab(activeTabId, {
          title: trimmedTitle,
          codeA: codeFileA.value,
          codeB: codeFileB.value,
          isDirty: false,
        })
        prevSourceCodeA.current = codeFileA.value
        prevSourceCodeB.current = codeFileB.value
        setActiveTabIsDirty(false)
        setSaveTitle(trimmedTitle)
        saveTitleManuallyEditedRef.current = false
        setEditingTrackId(null)
        setStatus('Loop updated!')
      }
      else {
        // Check if a track with the same title exists
        const existingTrack = tracks.find(t => t.title === trimmedTitle)
        if (existingTrack) {
          // Update existing track with same title
          await api.tracks.update(existingTrack.id, {
            title: trimmedTitle,
            code: channelMode === 'single' ? codeFileA.value : codeFileA.value,
            isPublic: savePublic,
          })
          updateTab(activeTabId, {
            title: trimmedTitle,
            codeA: codeFileA.value,
            codeB: codeFileB.value,
            isDirty: false,
          })
          prevSourceCodeA.current = codeFileA.value
          prevSourceCodeB.current = codeFileB.value
          setActiveTabIsDirty(false)
          setSaveTitle(trimmedTitle)
          saveTitleManuallyEditedRef.current = false
          setStatus('Loop updated!')
        }
        else {
          // Create new track
          const remixOfId = saveAsRemix ? currentTrackId : undefined
          await api.tracks.save(
            trimmedTitle,
            channelMode === 'single' ? codeFileA.value : codeFileA.value,
            savePublic,
            remixOfId,
          )
          updateTab(activeTabId, {
            title: trimmedTitle,
            codeA: codeFileA.value,
            codeB: codeFileB.value,
            isDirty: false,
          })
          prevSourceCodeA.current = codeFileA.value
          prevSourceCodeB.current = codeFileB.value
          setActiveTabIsDirty(false)
          setSaveTitle(trimmedTitle)
          saveTitleManuallyEditedRef.current = false
          setStatus('Loop saved!')
        }
      }

      setSavePublic(true)
      setSaveAsRemix(false)
      await loadTracks()

      if (activeTabId) {
        requestAnimationFrame(() => {
          scrollTabIntoView(activeTabId)
        })
      }
    }
    catch (err: any) {
      setError(err.message)
      setStatus('Error')
    }
    finally {
      setSaving(false)
    }
  }

  const handleUpdateCurrentTrack = async () => {
    if (!currentTrackId || !currentTrack || !user || currentTrack.userId !== user.id) return
    const trimmedTitle = saveTitle.trim()
    if (!trimmedTitle) {
      alert('Please enter a title for your loop')
      return
    }
    try {
      setSaving(true)
      setError('')
      await api.tracks.update(currentTrackId, {
        title: trimmedTitle,
        code: channelMode === 'single' ? codeFileA.value : codeFileA.value,
      })

      // Update the current tab's title and code
      updateTab(activeTabId, {
        title: trimmedTitle,
        codeA: codeFileA.value,
        codeB: codeFileB.value,
        isDirty: false,
      })
      prevSourceCodeA.current = codeFileA.value
      prevSourceCodeB.current = codeFileB.value
      setActiveTabIsDirty(false)
      setSaveTitle(trimmedTitle)
      saveTitleManuallyEditedRef.current = false

      // Update currentTrack state
      setCurrentTrack({ ...currentTrack, title: trimmedTitle })

      setStatus('Track updated!')
      await loadTracks()

      if (activeTabId) {
        requestAnimationFrame(() => {
          scrollTabIntoView(activeTabId)
        })
      }
    }
    catch (err: any) {
      setError(err.message)
      setStatus('Error')
      // If parent track not found, uncheck the remix checkbox
      if (err.message?.includes('Parent track not found')) {
        setSaveAsRemix(false)
      }
    }
    finally {
      setSaving(false)
    }
  }

  const handleSaveNewTrack = async (isRemix = false) => {
    const trimmedTitle = saveTitle.trim()
    if (!trimmedTitle) {
      alert('Please enter a title for your loop')
      return
    }
    try {
      setSaving(true)
      setError('')
      const remixOfId = isRemix ? currentTrackId : undefined
      const response = (await api.tracks.save(
        trimmedTitle,
        channelMode === 'single' ? codeFileA.value : codeFileA.value,
        savePublic,
        remixOfId,
      )) as { track: Track }
      const savedTrack = response.track

      // Update the active tab with the new track info
      updateTab(activeTabId, {
        title: savedTrack.title,
        artist: savedTrack.userName || 'Unknown',
        trackId: savedTrack.id,
        codeA: codeFileA.value,
        codeB: codeFileB.value,
        isDirty: false,
      })
      prevSourceCodeA.current = codeFileA.value
      prevSourceCodeB.current = codeFileB.value
      setActiveTabIsDirty(false)
      setSaveTitle(savedTrack.title)
      saveTitleManuallyEditedRef.current = false

      // Switch to Update flow
      setCurrentTrackId(savedTrack.id)
      setCurrentTrack(savedTrack)
      setSaveAsRemix(Boolean(savedTrack.remixOf))

      // Load parent track if this is a remix, so the "Remix of" message displays correctly
      if (savedTrack.remixOf) {
        try {
          const parentResponse = await api.tracks.getTrack(savedTrack.remixOf)
          setTrackParents(prev => ({
            ...prev,
            [savedTrack.id]: parentResponse.track,
          }))
        }
        catch (err) {
          console.error('Failed to load parent track:', err)
        }
      }

      setStatus('Track saved!')
      setSaveTitle(savedTrack.title)
      await loadTracks()

      if (activeTabId) {
        requestAnimationFrame(() => {
          scrollTabIntoView(activeTabId)
        })
      }
    }
    catch (err: any) {
      setError(err.message)
      setStatus('Error')
      // If parent track not found, uncheck the remix checkbox
      if (err.message?.includes('Parent track not found')) {
        setSaveAsRemix(false)
      }
    }
    finally {
      setSaving(false)
    }
  }

  const handleGenerateTitle = async () => {
    try {
      setGeneratingTitle(true)
      setError('')
      const currentCode = codeFileA.value
      if (!currentCode.trim()) {
        alert('Please add some code first')
        return
      }

      const response = await fetch('/api/ai/generate-title', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ code: currentCode }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to generate title' }))
        throw new Error(error.error || 'Failed to generate title')
      }

      const data = await response.json()
      if (data.title) {
        saveTitleManuallyEditedRef.current = true
        setSaveTitle(data.title)
        if (activeTab) {
          updateTab(activeTabId, { title: data.title, isDirty: true })
        }
        setStatus('Title generated!')
      }
    }
    catch (err: any) {
      setError(err.message)
      setStatus('Error generating title')
    }
    finally {
      setGeneratingTitle(false)
    }
  }

  const handleEditTrack = (track: Track) => {
    // Always create a new tab for editing
    const newTab: EditorTab = {
      id: `tab-${Date.now()}`,
      title: track.title,
      artist: track.userName || 'Unknown',
      codeA: track.code,
      codeB: track.code,
      editingTrackId: track.id,
      isDirty: false,
    }

    addTab(newTab)
    setActiveTabId(newTab.id)
    setSaveAsRemix(false)
    setSavePublic(track.isPublic)
    if (!hasNoAccount) {
      setSidebarTab('tracks')
    }

    // Scroll the new tab into view
    requestAnimationFrame(() => {
      scrollTabIntoView(newTab.id)
    })

    // Update refs to prevent the sync effect from interfering
    setTimeout(() => {
      prevSourceCodeA.current = track.code
      prevSourceCodeB.current = track.code
      handleCompile(track.code, track.code)
    }, 0)
  }

  const handleLoadTrack = (track: Track) => {
    // Clear any existing track loading timeout
    if (trackLoadingTimeoutRef.current) {
      clearTimeout(trackLoadingTimeoutRef.current)
    }
    // Mark that a track is being loaded - disable AutoCompileManager for 500ms
    trackLoadingTimeoutRef.current = setTimeout(() => {
      trackLoadingTimeoutRef.current = null
    }, 500)

    // Clear status error, but let AutoCompileManager handle editor errors
    setError('')

    // Always create a new tab for loading
    const newTab: EditorTab = {
      id: `tab-${Date.now()}`,
      title: track.title,
      artist: track.userName || 'Unknown',
      codeA: track.code,
      codeB: track.code,
      trackId: track.id,
      isDirty: false,
    }

    addTab(newTab)
    setActiveTabId(newTab.id)
    setSaveAsRemix(true)
    setSavePublic(track.isPublic)
    setCurrentTrack(track)

    // Check if this track is currently playing from the sidebar
    const wasPlayingFromSidebar = activeSidebarTrackId === track.id && isPlaying

    // Update refs to prevent the sync effect from interfering
    prevSourceCodeA.current = track.code
    prevSourceCodeB.current = track.code

    // Scroll the new tab into view
    requestAnimationFrame(() => {
      scrollTabIntoView(newTab.id)
    })

    // If playing from sidebar, switch to editor playback
    if (wasPlayingFromSidebar) {
      setPlayingTabId(newTab.id)
      setActiveSidebarTrackId(null)
      setSidebarPlayingTrackId(null)
      // Mark that playback was initiated from the editor
      setEditorInitiatedPlayback(true)
      setEditorPlayingTabId(newTab.id)
    }

    setTimeout(async () => {
      try {
        if (channelMode === 'dual') {
          const programA = compiler.compile(track.code)
          const programB = compiler.compile(track.code)
          updateProgramInfo(track.code, track.code)

          // If was playing from sidebar, reload with editor code (hot-reload)
          if (wasPlayingFromSidebar && dspscript.isPlaying()) {
            if (!dspscript.getAudioContext()) {
              await dspscript.init(latencyHint)
            }
            await dspscript.loadDualProgram(
              track.code,
              track.code,
              globalBpm,
              useCrossfadeStore.getState().crossfade,
            )
            setStatus('Playing')
          }
          else {
            setStatus(`Loaded: ${track.title}`)
          }
        }
        else {
          const programA = compiler.compile(track.code)
          updateProgramInfo(track.code, '')

          // If was playing from sidebar, reload with editor code (hot-reload)
          if (wasPlayingFromSidebar && dspscript.isPlaying()) {
            if (!dspscript.getAudioContext()) {
              await dspscript.init(latencyHint)
            }
            await dspscript.loadProgram(track.code)
            setStatus('Playing')
          }
          else {
            setStatus(`Loaded: ${track.title}`)
          }
        }

        // Track compiled successfully - let AutoCompileManager handle error clearing
        requestAnimationFrame(() => {
          setError('')
          updateTrackError(track.id, null)
        })
      }
      catch (err: any) {
        console.error(err)
        const editorErrors = convertToEditorErrors(err, track.code)
        setErrorsA(editorErrors)
        setErrorsB([])
        showError(editorErrors[0]?.message || err.message)
        updateTrackError(track.id, editorErrors[0]?.message || err.message || 'Error')
        setStatus('Error')
        if (wasPlayingFromSidebar) {
          setIsPlaying(false)
          setPlayingTabId(null)
        }
      }
    }, 100)
  }

  const handleLoadInA = (track: Track) => {
    // Clear any existing track loading timeout
    if (trackLoadingTimeoutRef.current) {
      clearTimeout(trackLoadingTimeoutRef.current)
    }
    // Mark that a track is being loaded - disable AutoCompileManager for 500ms
    trackLoadingTimeoutRef.current = setTimeout(() => {
      trackLoadingTimeoutRef.current = null
    }, 500)

    // Clear status error, but let AutoCompileManager handle editor errors
    setError('')

    // Check if this track is currently playing from the sidebar
    const wasPlayingFromSidebar = activeSidebarTrackId === track.id && isPlaying
    if (wasPlayingFromSidebar) {
      setActiveSidebarTrackId(null)
      setSidebarPlayingTrackId(null)
    }

    if (channelMode === 'dual') {
      // In dual mode, directly update the CodeFile
      const dualFiles = getDualModeCodeFiles()
      dualFiles.a.value = track.code
      prevSourceCodeA.current = track.code
      // Explicitly save to store
      setDualModeCodeA(track.code, dualFiles.a.getState(), track.title, track.userName)
    }
    else {
      // In single mode, switch to dual mode and load into A
      setChannelMode('dual')
      const dualFiles = getDualModeCodeFiles()
      const dualStore = getDualModeStore()
      // Keep B as it was (from dual mode store if exists, otherwise keep current dual mode B code)
      const codeForB = dualStore.codeB || dualFiles.b.value
      // Set A to the track code, keep B as it was
      dualFiles.a.value = track.code
      dualFiles.b.value = codeForB
      prevSourceCodeA.current = track.code
      prevSourceCodeB.current = codeForB
      // Save to store
      setDualModeCodeA(track.code, dualFiles.a.getState(), track.title, track.userName)
      setDualModeCodeB(codeForB, dualFiles.b.getState(), dualStore.titleB || 'Untitled', dualStore.artistB || 'Unknown')
    }

    setTimeout(async () => {
      try {
        // After switching to dual mode, always compile as dual
        const currentMode = getChannelModeStore().channelMode
        if (currentMode === 'dual') {
          const dualFiles = getDualModeCodeFiles()
          let programA
          let programB

          try {
            programA = compiler.compile(dualFiles.a.value)
          }
          catch (errA: any) {
            console.error(errA)
            // Don't set error states here - AutoCompileManager will handle it
            setStatus('Error')
            // Don't stop playback on compilation errors - keep playing the last good program
            return
          }

          try {
            programB = compiler.compile(dualFiles.b.value)
          }
          catch (errB: any) {
            console.error(errB)
            // Don't set error states here - AutoCompileManager will handle it
            setStatus('Error')
            // Don't stop playback on compilation errors - keep playing the last good program
            return
          }

          updateProgramInfo(dualFiles.a.value, dualFiles.b.value)

          // If was playing from sidebar, hot-reload with editor code
          if (wasPlayingFromSidebar && dspscript.isPlaying()) {
            if (!dspscript.getAudioContext()) {
              await dspscript.init(latencyHint)
            }
            await dspscript.loadDualProgram(
              dualFiles.a.value,
              dualFiles.b.value,
              globalBpm,
              useCrossfadeStore.getState().crossfade,
            )
            setStatus('Playing')
          }
          else {
            setStatus(`Loaded: ${track.title}`)
          }
        }
        else {
          const programA = compiler.compile(track.code)
          updateProgramInfo(track.code, '')
          setStatus(`Loaded: ${track.title}`)
        }
        // Track compiled successfully - let AutoCompileManager handle error clearing
        setError('')
        updateTrackError(track.id, null)
      }
      catch (err: any) {
        console.error(err)
        const editorErrors = convertToEditorErrors(err, track.code)
        setErrorsA(editorErrors)
        setErrorsB([])
        showError(editorErrors[0]?.message || err.message)
        updateTrackError(track.id, editorErrors[0]?.message || err.message || 'Error')
        setStatus('Error')
        // Don't stop playback on compilation errors - keep playing the last good program
      }
    }, 100)
  }

  const handleLoadInB = (track: Track) => {
    // Clear any existing track loading timeout
    if (trackLoadingTimeoutRef.current) {
      clearTimeout(trackLoadingTimeoutRef.current)
    }
    // Mark that a track is being loaded - disable AutoCompileManager for 500ms
    trackLoadingTimeoutRef.current = setTimeout(() => {
      trackLoadingTimeoutRef.current = null
    }, 500)

    // Clear status error, but let AutoCompileManager handle editor errors
    setError('')

    // Check if this track is currently playing from the sidebar
    const wasPlayingFromSidebar = activeSidebarTrackId === track.id && isPlaying
    if (wasPlayingFromSidebar) {
      setActiveSidebarTrackId(null)
      setSidebarPlayingTrackId(null)
    }

    if (channelMode === 'dual') {
      // In dual mode, directly update the CodeFile
      const dualFiles = getDualModeCodeFiles()
      dualFiles.b.value = track.code
      prevSourceCodeB.current = track.code
      // Explicitly save to store
      setDualModeCodeB(track.code, dualFiles.b.getState(), track.title, track.userName)
    }
    else {
      // In single mode, switch to dual mode and load into B
      setChannelMode('dual')
      const dualFiles = getDualModeCodeFiles()
      const dualStore = getDualModeStore()
      // Keep A as it was (from dual mode store if exists, otherwise keep current dual mode A code)
      const codeForA = dualStore.codeA || dualFiles.a.value
      // Keep A as it was, set B to the track code
      dualFiles.a.value = codeForA
      dualFiles.b.value = track.code
      prevSourceCodeA.current = codeForA
      prevSourceCodeB.current = track.code
      // Save to store
      setDualModeCodeA(codeForA, dualFiles.a.getState(), dualStore.titleA || 'Untitled', dualStore.artistA || 'Unknown')
      setDualModeCodeB(track.code, dualFiles.b.getState(), track.title, track.userName)
    }

    setTimeout(async () => {
      try {
        // After switching to dual mode, always compile as dual
        const currentMode = getChannelModeStore().channelMode
        if (currentMode === 'dual') {
          const dualFiles = getDualModeCodeFiles()
          let programA
          let programB

          try {
            programA = compiler.compile(dualFiles.a.value)
          }
          catch (errA: any) {
            console.error(errA)
            // Don't set error states here - AutoCompileManager will handle it
            setStatus('Error')
            // Don't stop playback on compilation errors - keep playing the last good program
            return
          }

          try {
            programB = compiler.compile(dualFiles.b.value)
          }
          catch (errB: any) {
            console.error(errB)
            // Don't set error states here - AutoCompileManager will handle it
            setStatus('Error')
            // Don't stop playback on compilation errors - keep playing the last good program
            return
          }

          updateProgramInfo(dualFiles.a.value, dualFiles.b.value)

          // If was playing from sidebar, hot-reload with editor code
          if (wasPlayingFromSidebar && dspscript.isPlaying()) {
            if (!dspscript.getAudioContext()) {
              await dspscript.init(latencyHint)
            }
            await dspscript.loadDualProgram(
              dualFiles.a.value,
              dualFiles.b.value,
              globalBpm,
              useCrossfadeStore.getState().crossfade,
            )
            setStatus('Playing')
          }
          else {
            setStatus(`Loaded: ${track.title}`)
          }
        }
        else {
          const programA = compiler.compile(track.code)
          updateProgramInfo(track.code, '')
          setStatus(`Loaded: ${track.title}`)
        }
        // Track compiled successfully - let AutoCompileManager handle error clearing
        setError('')
        updateTrackError(track.id, null)
      }
      catch (err: any) {
        console.error(err)
        const editorErrors = convertToEditorErrors(err, track.code)
        setErrorsB(editorErrors)
        setErrorsA([])
        showError(editorErrors[0]?.message || err.message)
        updateTrackError(track.id, editorErrors[0]?.message || err.message || 'Error')
        setStatus('Error')
        // Don't stop playback on compilation errors - keep playing the last good program
      }
    }, 100)
  }

  const handlePlaySidebarTrack = async (track: Track) => {
    try {
      setActiveSidebarTrackId(track.id)
      setPlayingTabId(null)
      setError('')

      // Lazy compilation check for My Tracks
      try {
        compiler.compile(track.code)
        updateTrackError(track.id, null)
      }
      catch (compileErr: any) {
        updateTrackError(track.id, compileErr.message || 'Compilation error')
        setActiveSidebarTrackId(null)
        return
      }

      if (!dspscript.getAudioContext()) {
        await dspscript.init(latencyHint)
      }

      await dspscript.loadNewProgram(track.code)
      await dspscript.start()
      setIsPlaying(true)
      setSidebarPlayingTrackId(track.id)
    }
    catch (err: any) {
      console.error(err)
      updateTrackError(track.id, err.message || 'Error')
      setActiveSidebarTrackId(null)
    }
  }

  const handleStopSidebarTrack = async () => {
    try {
      await dspscript.stop()
      setIsPlaying(false)
      setActiveSidebarTrackId(null)
      setSidebarPlayingTrackId(null)
    }
    catch (err: any) {
      console.error(err)
      showError(err.message)
    }
  }

  const handleDeleteTrack = async (trackId: string) => {
    const trackToDelete = tracks.find(t => t.id === trackId)

    if (!confirm(`Are you sure you want to delete "${trackToDelete?.title}"?`)) return

    try {
      await api.tracks.delete(trackId)
      await loadTracks()

      if (currentTrackId === trackId) {
        setCurrentTrackId(undefined)
        setCurrentTrack(null)
        setSaveAsRemix(false)
        updateTab(activeTabId, {
          trackId: undefined,
        })
      }
    }
    catch (err: any) {
      setError(err.message)
    }
  }

  const handleTogglePublic = async (track: Track) => {
    try {
      await api.tracks.update(track.id, { isPublic: !track.isPublic })
      await loadTracks()
    }
    catch (err: any) {
      setError(err.message)
    }
  }

  const handleVisibilityToggle = async (newValue: boolean) => {
    setSavePublic(newValue)

    // If it's the user's own track and exists, update immediately
    if (currentTrackId && currentTrack && user && currentTrack.userId === user.id) {
      try {
        await api.tracks.update(currentTrackId, { isPublic: newValue })
        // Update currentTrack state to reflect the change
        setCurrentTrack({ ...currentTrack, isPublic: newValue })
        await loadTracks()
      }
      catch (err: any) {
        setError(err.message)
        // Revert on error
        setSavePublic(!newValue)
      }
    }
  }

  const handleSaveAsRemix = async () => {
    await handleSaveNewTrack(true)
  }

  // Memoize SaveForm callbacks - must be defined after all handler functions to avoid initialization order issues
  const handleSaveTitleChange = useCallback(
    (value: string) => {
      saveTitleManuallyEditedRef.current = true
      setSaveTitle(value)
      if (activeTab && value !== activeTab.title) {
        updateTab(activeTabId, { isDirty: true })
      }
    },
    [activeTab, activeTabId, updateTab],
  )

  const handleSaveTitleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (!saving && saveButtonState.hasChanges) {
          if (currentTrack && user && currentTrack.userId === user.id) {
            handleUpdateCurrentTrack()
          }
          else {
            handleSaveNewTrack()
          }
        }
      }
    },
    [
      saving,
      saveButtonState.hasChanges,
      currentTrack,
      user,
      handleUpdateCurrentTrack,
      handleSaveNewTrack,
    ],
  )

  const handleToggleSaveMenu = useCallback(() => setSaveMenuOpen(prev => !prev), [])

  const handleSave = useCallback(() => {
    if (!user) {
      setShowAuthModal(true)
    }
    else if (currentTrack && user && currentTrack.userId === user.id) {
      handleUpdateCurrentTrack()
    }
    else {
      handleSaveNewTrack()
    }
  }, [user, currentTrack, handleUpdateCurrentTrack, handleSaveNewTrack])

  const handleExport = useCallback(() => {
    try {
      const program = compiler.compile(codeFileARef.current!.value)
      setExportBpm(program.bpm)
      setShowExportModal(true)
    }
    catch (err: any) {
      setError('Please fix compilation errors before exporting')
    }
  }, [])

  const handleDeleteTrackCallback = useCallback(() => {
    if (currentTrackId) {
      handleDeleteTrack(currentTrackId)
    }
  }, [currentTrackId, handleDeleteTrack])

  const handleDownloadAllTracksZip = async () => {
    if (!user) return

    try {
      setError('')
      const blob = await api.tracks.downloadAllTracksZip()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'tracks.zip'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      setStatus('Downloaded all tracks')
    }
    catch (err: any) {
      setError(err.message)
      setStatus('Error')
    }
  }

  const handleToggleLike = async (track: Track) => {
    if (!user) return

    // Prevent users from liking their own tracks
    if (track.userId === user.id) return

    const isLiked = likedTrackIds.has(track.id)
    isTogglingLikeRef.current = true

    try {
      // Optimistically update UI
      if (isLiked) {
        setLikedTrackIds(prev => {
          const newSet = new Set(prev)
          newSet.delete(track.id)
          return newSet
        })
        // Update like count in tracks state
        setTracks(prev => prev.map(t => t.id === track.id ? { ...t, likesCount: Math.max(0, t.likesCount - 1) } : t))
        // Update like count in liked tracks state (keep track in list)
        setLikedTracks(prev =>
          prev.map(t =>
            t.id === track.id
              ? { ...t, likesCount: Math.max(0, t.likesCount - 1), isLiked: false }
              : t
          )
        )
        await api.tracks.unlike(track.id)
      }
      else {
        setLikedTrackIds(prev => new Set(prev).add(track.id))
        // Update like count in tracks state
        setTracks(prev => prev.map(t => (t.id === track.id ? { ...t, likesCount: t.likesCount + 1 } : t)))
        // Add to liked tracks list if not already present
        setLikedTracks(prev => {
          if (prev.some(t => t.id === track.id)) {
            return prev.map(t => t.id === track.id ? { ...t, likesCount: t.likesCount + 1, isLiked: true } : t)
          }
          return [...prev, { ...track, likesCount: track.likesCount + 1, isLiked: true }]
        })
        await api.tracks.like(track.id)
      }
    }
    catch (err: any) {
      setError(err.message)
      // Revert optimistic update on error
      if (isLiked) {
        setLikedTrackIds(prev => new Set(prev).add(track.id))
        setTracks(prev => prev.map(t => (t.id === track.id ? { ...t, likesCount: t.likesCount + 1 } : t)))
        // Update like count in liked tracks state (keep track in list)
        setLikedTracks(prev =>
          prev.map(t => t.id === track.id ? { ...t, likesCount: t.likesCount + 1, isLiked: true } : t)
        )
      }
      else {
        setLikedTrackIds(prev => {
          const newSet = new Set(prev)
          newSet.delete(track.id)
          return newSet
        })
        setTracks(prev => prev.map(t => t.id === track.id ? { ...t, likesCount: Math.max(0, t.likesCount - 1) } : t))
        // Update like count in liked tracks state (keep track in list)
        setLikedTracks(prev =>
          prev.map(t =>
            t.id === track.id
              ? { ...t, likesCount: Math.max(0, t.likesCount - 1), isLiked: false }
              : t
          )
        )
      }
    }
    finally {
      // Reset the flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isTogglingLikeRef.current = false
      }, 100)
    }
  }

  const updateProgramInfo = useCallback((sourceA: string, sourceB: string) => {
    const seqMatchesA = sourceA.match(/seq\(/g)
    const voicesA = seqMatchesA ? seqMatchesA.length : 0
    const seqMatchesB = sourceB.match(/seq\(/g)
    const voicesB = seqMatchesB ? seqMatchesB.length : 0

    const ctx = dspscript.getAudioContext()
    const sampleRateValue = ctx ? ctx.sampleRate : 44100

    setSampleRate(sampleRateValue.toString())
    setInstrCount(`${voicesA}+${voicesB} voice(s)`)
  }, [])

  const showError = useCallback((message: string) => {
    if (message.match(/\(at line \d+/)) {
      setError('Error: ' + message)
    }
    else {
      setError('Error: ' + adjustErrorLineNumbers(message))
    }
  }, [])

  // Stable callback for clearing errors
  const handleClearError = useCallback(() => {
    setError('')
  }, [])

  useEffect(() => {
    const loadExample = async () => {
      // Check for remix track FIRST, before any other logic
      // This ensures remix tracks are loaded even on first visit
      const remixTrackData = getRemixTrack()
      if (remixTrackData) {
        // Remove skipExampleOnce if it exists - we'll process remixTrack instead
        removeSkipExampleOnce()
        // Process remixTrack immediately - this code is duplicated below but we need it here first
        try {
          const {
            code,
            trackId,
            title,
            artist,
            loadIn,
            wasPlaying,
            autoCompileAndPlay,
            trackData,
          } = remixTrackData
          console.log('[App] Processing remixTrack on first load:', {
            trackId,
            title,
            loadIn,
            wasPlaying,
            autoCompileAndPlay,
            channelMode,
          })

          // Set the current track from the loaded data
          if (trackData) {
            setCurrentTrack(trackData)
          }

          // Check if this track was playing from PublicTracks
          const wasPlayingFromPublicTracks = wasPlaying
            && (getPublicTracksActiveTrackId() === trackId
              || getPublicTracksActiveRemixId() === trackId)

          // Track the new tab ID for use in setTimeout
          let newTabId: string | null = null

          // Create a new tab or update current tab based on loadIn
          if (channelMode === 'dual') {
            // In dual mode, update CodeFiles directly
            const dualFiles = getDualModeCodeFiles()
            if (loadIn === 'A') {
              dualFiles.a.value = code
              prevSourceCodeA.current = code
              setDualModeCodeA(code, dualFiles.a.getState(), title, artist)
            }
            else if (loadIn === 'B') {
              dualFiles.b.value = code
              prevSourceCodeB.current = code
              setDualModeCodeB(code, dualFiles.b.getState(), title, artist)
            }
            else {
              dualFiles.a.value = code
              dualFiles.b.value = code
              prevSourceCodeA.current = code
              prevSourceCodeB.current = code
              setDualModeCodeA(code, dualFiles.a.getState(), title, artist)
              setDualModeCodeB(code, dualFiles.b.getState(), title, artist)
            }
          }
          else {
            // In single mode, use tabs
            if (loadIn === 'A') {
              if (activeTab) {
                updateTab(activeTabId, { codeA: code, isDirty: true })
                prevSourceCodeA.current = code
                lastCompiledCodeA.current = code
                codeFileA.value = code
                if (wasPlayingFromPublicTracks) {
                  setActiveTabId(activeTabId)
                  setPlayingTabId(activeTabId)
                  setEditorInitiatedPlayback(true)
                  setEditorPlayingTabId(activeTabId)
                  removePublicTracksActiveTrackId()
                  removePublicTracksActiveRemixId()
                }
                else if (wasPlaying) {
                  setPlayingTabId(activeTabId)
                }
              }
              else {
                newTabId = `tab-${Date.now()}`
                const newTab: EditorTab = {
                  id: newTabId,
                  title: title || 'Untitled',
                  artist: artist || '',
                  codeA: code,
                  codeB: codeFileB.value,
                  trackId: trackId,
                  isDirty: false,
                }
                addTab(newTab)
                setActiveTabId(newTabId)
                const tabIdToScroll = newTabId
                prevSourceCodeA.current = code
                lastCompiledCodeA.current = code
                codeFileA.value = code
                requestAnimationFrame(() => {
                  scrollTabIntoView(tabIdToScroll)
                })
                if (wasPlayingFromPublicTracks) {
                  setPlayingTabId(newTabId)
                  setEditorInitiatedPlayback(true)
                  setEditorPlayingTabId(newTabId)
                  removePublicTracksActiveTrackId()
                  removePublicTracksActiveRemixId()
                }
                else if (wasPlaying) {
                  setPlayingTabId(newTabId)
                }
              }
            }
            else if (loadIn === 'B') {
              if (activeTab) {
                updateTab(activeTabId, { codeB: code, isDirty: true })
                prevSourceCodeB.current = code
                lastCompiledCodeB.current = code
                codeFileB.value = code
                if (wasPlayingFromPublicTracks) {
                  setActiveTabId(activeTabId)
                  setPlayingTabId(activeTabId)
                  setEditorInitiatedPlayback(true)
                  setEditorPlayingTabId(activeTabId)
                  removePublicTracksActiveTrackId()
                  removePublicTracksActiveRemixId()
                }
                else if (wasPlaying) {
                  setPlayingTabId(activeTabId)
                }
              }
              else {
                newTabId = `tab-${Date.now()}`
                const newTab: EditorTab = {
                  id: newTabId,
                  title: title || 'Untitled',
                  artist: artist || '',
                  codeA: codeFileA.value,
                  codeB: code,
                  trackId: trackId,
                  isDirty: false,
                }
                addTab(newTab)
                setActiveTabId(newTabId)
                const tabIdToScroll = newTabId
                prevSourceCodeB.current = code
                lastCompiledCodeB.current = code
                codeFileB.value = code
                requestAnimationFrame(() => {
                  scrollTabIntoView(tabIdToScroll)
                })
                if (wasPlayingFromPublicTracks) {
                  setPlayingTabId(newTabId)
                  setEditorInitiatedPlayback(true)
                  setEditorPlayingTabId(newTabId)
                  removePublicTracksActiveTrackId()
                  removePublicTracksActiveRemixId()
                }
                else if (wasPlaying) {
                  setPlayingTabId(newTabId)
                }
              }
            }
            else {
              newTabId = `tab-${Date.now()}`
              const newTab: EditorTab = {
                id: newTabId,
                title: title || 'Untitled',
                artist: artist || '',
                codeA: code,
                codeB: code,
                trackId: trackId,
                isDirty: false,
              }
              addTab(newTab)
              setActiveTabId(newTabId)
              const tabIdToScroll = newTabId
              prevSourceCodeA.current = code
              prevSourceCodeB.current = code
              lastCompiledCodeA.current = code
              lastCompiledCodeB.current = code
              codeFileA.value = code
              codeFileB.value = code
              requestAnimationFrame(() => {
                scrollTabIntoView(tabIdToScroll)
              })
              if (wasPlayingFromPublicTracks) {
                setPlayingTabId(newTabId)
                setEditorInitiatedPlayback(true)
                setEditorPlayingTabId(newTabId)
                removePublicTracksActiveTrackId()
                removePublicTracksActiveRemixId()
              }
              else if (wasPlaying) {
                setPlayingTabId(newTabId)
              }
            }
          }

          setCurrentTrackId(trackId)
          if (wasPlaying && dspscript.isPlaying()) {
            setIsPlaying(true)
          }
          removeRemixTrack()
          setSkipExampleOnce(true)
          ;(async () => {
            try {
              const shouldAutoplay = getAutoplayOnLoad()
              removeAutoplayOnLoad()

              if (channelMode === 'dual') {
                console.log('[App] Processing remixTrack - dual mode', {
                  loadIn,
                  autoCompileAndPlay,
                  isPlaying: dspscript.isPlaying(),
                  shouldAutoplay,
                  wasPlayingFromPublicTracks,
                })
                const dualFiles = getDualModeCodeFiles()
                const codeA = loadIn === 'A' ? code : dualFiles.a.value
                const codeB = loadIn === 'B' ? code : dualFiles.b.value

                const programA = compiler.compile(codeA)
                const programB = compiler.compile(codeB)
                updateProgramInfo(codeA, codeB)

                if (wasPlayingFromPublicTracks && dspscript.isPlaying()) {
                  if (dspscript.getAudioContext()) {
                    await dspscript.loadDualProgram(
                      codeA,
                      codeB,
                      globalBpm,
                      useCrossfadeStore.getState().crossfade,
                    )
                    setIsPlaying(true)
                    setPlayingTabId(null)
                    setEditorInitiatedPlayback(true)
                    setStatus('Playing')
                  }
                }
                else if (autoCompileAndPlay && dspscript.isPlaying()) {
                  console.log('[App] Auto-reloading dual program')
                  if (dspscript.getAudioContext()) {
                    await dspscript.loadDualProgram(
                      codeA,
                      codeB,
                      globalBpm,
                      useCrossfadeStore.getState().crossfade,
                    )
                    setIsPlaying(true)
                    setPlayingTabId(null)
                    setEditorInitiatedPlayback(true)
                    setStatus('Playing')
                  }
                }
                else if (shouldAutoplay) {
                  if (!dspscript.getAudioContext()) {
                    await dspscript.init(latencyHint)
                  }
                  await dspscript.loadDualProgram(
                    codeA,
                    codeB,
                    globalBpm,
                    useCrossfadeStore.getState().crossfade,
                  )
                  // Set playingTabId before starting audio to avoid race condition
                  setPlayingTabId(null)
                  setEditorInitiatedPlayback(true)
                  await dspscript.start()
                  setIsPlaying(true)
                  setStatus('Playing')
                }
                else {
                  setStatus(`Loaded: ${title} (ready to remix)`)
                }
              }
              else {
                const programA = compiler.compile(code)
                updateProgramInfo(code, '')

                if (wasPlayingFromPublicTracks && dspscript.isPlaying()) {
                  if (dspscript.getAudioContext()) {
                    const tabId = newTabId || activeTabId
                    await dspscript.loadProgram(code)
                    setIsPlaying(true)
                    setPlayingTabId(tabId)
                    setEditorInitiatedPlayback(true)
                    setEditorPlayingTabId(tabId)
                    setStatus('Playing')
                  }
                }
                else if (shouldAutoplay) {
                  if (!dspscript.getAudioContext()) {
                    await dspscript.init(latencyHint)
                  }
                  const tabId = newTabId || activeTabId
                  await dspscript.loadProgram(code)
                  // Set playingTabId before starting audio to avoid race condition
                  setPlayingTabId(tabId)
                  setEditorInitiatedPlayback(true)
                  setEditorPlayingTabId(tabId)
                  await dspscript.start()
                  setIsPlaying(true)
                  setStatus('Playing')
                }
                else {
                  setStatus(`Loaded: ${title} (ready to remix)`)
                }
              }
              setError('')
              setErrorsA([])
              setErrorsB([])
            }
            catch (err: any) {
              console.error(err)
              const editorErrors = convertToEditorErrors(err, code)
              setErrorsA(editorErrors)
              showError(editorErrors[0]?.message || err.message)
              setStatus('Error')
              if (wasPlayingFromPublicTracks) {
                setIsPlaying(false)
                setPlayingTabId(null)
              }
            }
          })()
          return
        }
        catch (err) {
          console.error('Failed to load remix track:', err)
          removeRemixTrack()
        }
      }

      // Only check skipExampleOnce if there's no remixTrack
      const skipOnce = getSkipExampleOnce()
      if (skipOnce) {
        removeSkipExampleOnce()
        return
      }

      // If we already have persisted tabs with code, do not fetch the initial example
      try {
        const tabs = getTabs().tabs
        const hasExistingCode = tabs.some(
          t => (t.codeA && t.codeA.length) || (t.codeB && t.codeB.length),
        )
        if (hasExistingCode) {
          // Still handle remix track if present, but skip example fetch
          const remixTrackData = getRemixTrack()
          if (remixTrackData) {
            try {
              const {
                code,
                trackId,
                title,
                artist,
                loadIn,
                wasPlaying,
                autoCompileAndPlay,
                trackData,
              } = remixTrackData
              console.log('[App] hasExistingCode - Parsed remixTrack data:', {
                trackId,
                title,
                loadIn,
                wasPlaying,
                autoCompileAndPlay,
                channelMode,
              })

              // Set the current track from the loaded data
              if (trackData) {
                setCurrentTrack(trackData)
              }

              // Check if this track was playing from PublicTracks
              const wasPlayingFromPublicTracks = wasPlaying
                && (getPublicTracksActiveTrackId() === trackId
                  || getPublicTracksActiveRemixId() === trackId)

              // Track the new tab ID for use in setTimeout
              let newTabId: string | null = null

              if (channelMode === 'dual') {
                // In dual mode, update CodeFiles directly
                const dualFiles = getDualModeCodeFiles()
                if (loadIn === 'A') {
                  dualFiles.a.value = code
                  prevSourceCodeA.current = code
                  setDualModeCodeA(code, dualFiles.a.getState(), title, artist)
                }
                else if (loadIn === 'B') {
                  dualFiles.b.value = code
                  prevSourceCodeB.current = code
                  setDualModeCodeB(code, dualFiles.b.getState(), title, artist)
                }
                else {
                  dualFiles.a.value = code
                  dualFiles.b.value = code
                  prevSourceCodeA.current = code
                  prevSourceCodeB.current = code
                  setDualModeCodeA(code, dualFiles.a.getState(), title, artist)
                  setDualModeCodeB(code, dualFiles.b.getState(), title, artist)
                }
              }
              else {
                // In single mode, use tabs
                if (loadIn === 'A') {
                  if (activeTab) {
                    updateTab(activeTabId, { codeA: code, isDirty: true })
                    prevSourceCodeA.current = code
                    lastCompiledCodeA.current = code
                    codeFileA.value = code
                    if (wasPlayingFromPublicTracks) {
                      setActiveTabId(activeTabId)
                      setPlayingTabId(activeTabId)
                      setEditorInitiatedPlayback(true)
                      setEditorPlayingTabId(activeTabId)
                      removePublicTracksActiveTrackId()
                      removePublicTracksActiveRemixId()
                    }
                    else if (wasPlaying) {
                      setPlayingTabId(activeTabId)
                    }
                  }
                  else {
                    newTabId = `tab-${Date.now()}`
                    const newTab: EditorTab = {
                      id: newTabId,
                      title: title || 'Untitled',
                      artist: artist || '',
                      codeA: code,
                      codeB: codeFileB.value,
                      trackId: trackId,
                      isDirty: false,
                    }
                    addTab(newTab)
                    prevSourceCodeA.current = code
                    lastCompiledCodeA.current = code
                    codeFileA.value = code
                    if (wasPlayingFromPublicTracks) {
                      setActiveTabId(newTabId)
                      setPlayingTabId(newTabId)
                      setEditorInitiatedPlayback(true)
                      setEditorPlayingTabId(newTabId)
                      removePublicTracksActiveTrackId()
                      removePublicTracksActiveRemixId()
                    }
                    else if (wasPlaying) {
                      setPlayingTabId(newTabId)
                    }
                  }
                }
                else if (loadIn === 'B') {
                  if (activeTab) {
                    updateTab(activeTabId, { codeB: code, isDirty: true })
                    prevSourceCodeB.current = code
                    lastCompiledCodeB.current = code
                    codeFileB.value = code
                    if (wasPlayingFromPublicTracks) {
                      setActiveTabId(activeTabId)
                      setPlayingTabId(activeTabId)
                      setEditorInitiatedPlayback(true)
                      setEditorPlayingTabId(activeTabId)
                      removePublicTracksActiveTrackId()
                      removePublicTracksActiveRemixId()
                    }
                    else if (wasPlaying) {
                      setPlayingTabId(activeTabId)
                    }
                  }
                  else {
                    newTabId = `tab-${Date.now()}`
                    const newTab: EditorTab = {
                      id: newTabId,
                      title: title || 'Untitled',
                      artist: artist || '',
                      codeA: codeFileA.value,
                      codeB: code,
                      trackId: trackId,
                      isDirty: false,
                    }
                    addTab(newTab)
                    prevSourceCodeB.current = code
                    lastCompiledCodeB.current = code
                    codeFileB.value = code
                    if (wasPlayingFromPublicTracks) {
                      setActiveTabId(newTabId)
                      setPlayingTabId(newTabId)
                      setEditorInitiatedPlayback(true)
                      setEditorPlayingTabId(newTabId)
                      removePublicTracksActiveTrackId()
                      removePublicTracksActiveRemixId()
                    }
                    else if (wasPlaying) {
                      setPlayingTabId(newTabId)
                    }
                  }
                }
                else {
                  newTabId = `tab-${Date.now()}`
                  const newTab: EditorTab = {
                    id: newTabId,
                    title: title || 'Untitled',
                    artist: artist || '',
                    codeA: code,
                    codeB: code,
                    trackId: trackId,
                    isDirty: false,
                  }
                  addTab(newTab)
                  setActiveTabId(newTabId)
                  const tabIdToScroll = newTabId
                  prevSourceCodeA.current = code
                  prevSourceCodeB.current = code
                  lastCompiledCodeA.current = code
                  lastCompiledCodeB.current = code
                  codeFileA.value = code
                  codeFileB.value = code
                  requestAnimationFrame(() => {
                    scrollTabIntoView(tabIdToScroll)
                  })
                  if (wasPlayingFromPublicTracks) {
                    setPlayingTabId(newTabId)
                    setEditorInitiatedPlayback(true)
                    setEditorPlayingTabId(newTabId)
                    removePublicTracksActiveTrackId()
                    removePublicTracksActiveRemixId()
                  }
                  else if (wasPlaying) {
                    setPlayingTabId(newTabId)
                  }
                }
              }
              setCurrentTrackId(trackId)
              if (wasPlaying && dspscript.isPlaying()) {
                setIsPlaying(true)
              }
              removeRemixTrack()
              setSkipExampleOnce(true)

              // Handle auto-compile and play for dual mode when loading into A/B
              setTimeout(async () => {
                try {
                  if (channelMode === 'dual' && (loadIn === 'A' || loadIn === 'B')) {
                    const dualFiles = getDualModeCodeFiles()
                    const codeA = loadIn === 'A' ? code : dualFiles.a.value
                    const codeB = loadIn === 'B' ? code : dualFiles.b.value

                    let programA
                    let programB

                    try {
                      programA = compiler.compile(codeA)
                    }
                    catch (errA: any) {
                      const editorErrors = convertToEditorErrors(errA, codeA)
                      setErrorsA(editorErrors)
                      setErrorsB([])
                      showError(editorErrors[0]?.message || errA.message)
                      setStatus('Error')
                      if (wasPlayingFromPublicTracks) {
                        setIsPlaying(false)
                        setPlayingTabId(null)
                      }
                      return
                    }

                    try {
                      programB = compiler.compile(codeB)
                    }
                    catch (errB: any) {
                      const editorErrors = convertToEditorErrors(errB, codeB)
                      setErrorsA([])
                      setErrorsB(editorErrors)
                      showError(editorErrors[0]?.message || errB.message)
                      setStatus('Error')
                      if (wasPlayingFromPublicTracks) {
                        setIsPlaying(false)
                        setPlayingTabId(null)
                      }
                      return
                    }

                    updateProgramInfo(codeA, codeB)

                    if (wasPlayingFromPublicTracks && dspscript.isPlaying()) {
                      // Reload with editor code when switching from PublicTracks playback
                      if (dspscript.getAudioContext()) {
                        await dspscript.loadDualProgram(
                          codeA,
                          codeB,
                          globalBpm,
                          useCrossfadeStore.getState().crossfade,
                        )
                        setIsPlaying(true)
                        setPlayingTabId(null)
                        setEditorInitiatedPlayback(true)
                        setStatus('Playing')
                      }
                    }
                    else if (autoCompileAndPlay && dspscript.isPlaying()) {
                      console.log('[App] hasExistingCode - Auto-reloading dual program')
                      if (dspscript.getAudioContext()) {
                        await dspscript.loadDualProgram(
                          codeA,
                          codeB,
                          globalBpm,
                          useCrossfadeStore.getState().crossfade,
                        )
                        setIsPlaying(true)
                        setPlayingTabId(null)
                        setEditorInitiatedPlayback(true)
                        setStatus('Playing')
                      }
                    }
                    else {
                      setStatus(`Loaded: ${title} (ready to remix)`)
                    }
                  }
                  else if (channelMode === 'single') {
                    const programA = compiler.compile(code)
                    updateProgramInfo(code, '')

                    if (wasPlayingFromPublicTracks && dspscript.isPlaying()) {
                      // Reload with editor code when switching from PublicTracks playback
                      if (dspscript.getAudioContext()) {
                        const tabId = newTabId || activeTabId
                        await dspscript.loadProgram(code)
                        setIsPlaying(true)
                        setPlayingTabId(tabId)
                        setEditorInitiatedPlayback(true)
                        setEditorPlayingTabId(tabId)
                        setStatus('Playing')
                      }
                    }
                    else {
                      setStatus(`Loaded: ${title} (ready to remix)`)
                    }
                  }
                  setError('')
                  setErrorsA([])
                  setErrorsB([])
                }
                catch (err: any) {
                  console.error(err)
                  const editorErrors = convertToEditorErrors(err, code)
                  setErrorsA(editorErrors)
                  showError(editorErrors[0]?.message || err.message)
                  setStatus('Error')
                  if (wasPlayingFromPublicTracks) {
                    setIsPlaying(false)
                    setPlayingTabId(null)
                  }
                }
              }, 100)
            }
            catch (err) {
              console.error('Failed to load remix track:', err)
              removeRemixTrack()
            }
          }
          return
        }
      }
      catch {}

      // First, load the example code into the default tab
      try {
        const response = await fetch('./spec/spec-simple.txt?t=' + startupTime)
        const exampleCode = await response.text()

        // Update the default tab with the example code
        const tabs = getTabs().tabs
        const firstTab = tabs[0]
        if (firstTab && firstTab.id === 'default') {
          updateTab('default', { codeA: exampleCode, codeB: exampleCode })
        }

        // Set initial code state
        prevSourceCodeA.current = exampleCode
        prevSourceCodeB.current = exampleCode
        lastCompiledCodeA.current = exampleCode
        lastCompiledCodeB.current = exampleCode
        codeFileA.value = exampleCode
        codeFileB.value = exampleCode
        exampleCodeRef.current = exampleCode
      }
      catch (err) {
        console.error('Failed to load example:', err)
      }

      // RemixTrack is already handled at the beginning of this function
      // Compile the example code if no remix track was loaded
      // Only compile if nothing is currently playing (to avoid interrupting playback from PublicTracks)
      setTimeout(() => {
        // Check if audio is actually playing before auto-compiling
        if (dspscript.isPlaying()) {
          setStatus('Ready')
          return
        }

        try {
          const programA = compiler.compile(codeFileA.value)
          updateProgramInfo(codeFileA.value, '')
          setStatus('Compiled')
          setError('')
          setErrorsA([])
          setErrorsB([])
        }
        catch (err: any) {
          console.error(err)
          const editorErrors = convertToEditorErrors(err, codeFileA.value)
          setErrorsA(editorErrors)
          showError(editorErrors[0]?.message || err.message)
          setStatus('Error')
        }
      }, 100)
    }

    loadExample()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dspscript.setSyncMode(syncBars)
  }, [syncBars])

  // Sync playingTabId to store whenever it changes
  useEffect(() => {
    setPlayingTabIdInStore(playingTabId)
  }, [playingTabId, setPlayingTabIdInStore])

  // Scroll playing tab into view when playback starts or when playing tab changes
  useEffect(() => {
    if (!isPlaying) return
    if (!playingTabId) return
    scrollTabIntoView(playingTabId)
  }, [isPlaying, playingTabId])

  // Sync isPlaying state with actual playback state when component mounts
  // This ensures we don't interrupt playback when navigating back from PublicTracks
  useEffect(() => {
    const actualPlaying = dspscript.isPlaying()
    if (actualPlaying !== isPlaying) {
      setIsPlaying(actualPlaying)
    }

    // Restore playingTabId if audio is playing and editor was playing before
    // (indicated by editorPlayingTabId in session store, and not PublicTracks playing)
    // Don't restore if we're in dual mode or just switched from dual mode
    if (actualPlaying && channelMode === 'single') {
      const storedPlayingTabId = getEditorPlayingTabId()
      const publicTracksActive = getPublicTracksActiveTrackId() || getPublicTracksActiveRemixId()

      // Only restore if editor was playing (has stored tab ID) and PublicTracks is not playing
      if (storedPlayingTabId && !publicTracksActive) {
        // Verify the tab still exists
        const allTabs = getTabs().tabs
        const tabExists = allTabs.some(t => t.id === storedPlayingTabId)
        if (tabExists) {
          setPlayingTabId(storedPlayingTabId)
        }
        else {
          // Tab no longer exists, set to active tab
          setPlayingTabId(activeTabId)
          setEditorPlayingTabId(activeTabId)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Scroll active tab into view when tabs are loaded from state (initial load)
  useEffect(() => {
    if (channelMode !== 'single') return
    if (hasScrolledInitialTabRef.current) return
    const allTabs = getTabs().tabs
    if (!allTabs.length || !activeTabId) return

    // Wait for DOM to be ready
    const timer = setTimeout(() => {
      if (tabBarRef.current && !hasScrolledInitialTabRef.current) {
        scrollTabIntoView(activeTabId)
        hasScrolledInitialTabRef.current = true
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [channelMode, activeTabId])

  const handleCompile = (codeA?: string, codeB?: string, shouldReload: boolean = true): boolean => {
    const compileCodeA = codeA ?? codeFileARef.current!.value
    const compileCodeB = codeB ?? codeFileBRef.current!.value

    // Set loading state immediately to show spinner
    setLoading(true)
    const startTime = Date.now()
    const minLoadingDuration = 300 // minimum 300ms to show spinner

    const clearLoadingWithDelay = () => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, minLoadingDuration - elapsed)
      setTimeout(() => setLoading(false), remaining)
    }

    try {
      if (channelMode === 'dual') {
        let programA
        let programB
        try {
          programA = compiler.compile(compileCodeA)
        }
        catch (errA: any) {
          clearLoadingWithDelay()
          const editorErrors = convertToEditorErrors(errA, compileCodeA)
          editorErrorsARef.current = editorErrors
          editorErrorsBRef.current = []
          setErrorsA(editorErrors)
          setErrorsB([])
          const errorMsg = editorErrors[0]?.message || errA.message
          errorRef.current = errorMsg
          showError(errorMsg)
          setStatus('Error')
          return false
        }

        try {
          programB = compiler.compile(compileCodeB)
        }
        catch (errB: any) {
          clearLoadingWithDelay()
          const editorErrors = convertToEditorErrors(errB, compileCodeB)
          editorErrorsARef.current = []
          editorErrorsBRef.current = editorErrors
          setErrorsA([])
          setErrorsB(editorErrors)
          const errorMsg = editorErrors[0]?.message || errB.message
          errorRef.current = errorMsg
          showError(errorMsg)
          setStatus('Error')
          return false
        }

        updateProgramInfo(compileCodeA, compileCodeB)
        setStatus('Compiled')
        editorErrorsARef.current = []
        editorErrorsBRef.current = []
        errorRef.current = ''
        // Track compiled successfully - clear editor errors
        setError('')
        setErrorsA([])
        setErrorsB([])
        // Update numbers with params for sliders - save to tab
        const { updateTab, activeTabId: currentActiveTabId } = getTabs()
        updateTab(currentActiveTabId, {
          numbersWithParamsA: programA.numbersWithParams || [],
          numbersWithParamsB: programB.numbersWithParams || [],
          patternEventsA: programA.patternEvents || [],
          patternEventsB: programB.patternEvents || [],
          sequencesA: programA.sequences || [],
          sequencesB: programB.sequences || [],
          amplitudeInfosA: programA.amplitudeInfos || [],
          amplitudeInfosB: programB.amplitudeInfos || [],
          waveformInfosA: programA.waveformInfos || [],
          waveformInfosB: programB.waveformInfos || [],
          levelInfosA: programA.levelInfos || [],
          levelInfosB: programB.levelInfos || [],
          timelineInfosA: programA.timelineInfos || [],
          timelineInfosB: programB.timelineInfos || [],
        })
        console.log('[App] Updated tab with timelineInfosA:', programA.timelineInfos, 'timelineInfosB:',
          programB.timelineInfos)

        // Only reload if audio is playing AND we're in the editor (not from PublicTracks)
        // Check if audio is actually playing and if it was started from the editor
        const actualPlaying = dspscript.isPlaying()
        const editorInitiated = getEditorInitiatedPlayback()
        // In dual mode, there are no tabs, so we can reload if playing
        if (shouldReload && actualPlaying && dspscript.getAudioContext() && editorInitiated) {
          let loadingCleared = false
          const clearLoadingOnce = () => {
            if (!loadingCleared) {
              loadingCleared = true
              clearLoadingWithDelay()
            }
          }
          const timeoutId = setTimeout(() => {
            clearLoadingOnce()
            setStatus('Error')
            showError('Loading timeout')
          }, 10000)
          dspscript
            .loadDualProgram(
              compileCodeA,
              compileCodeB,
              globalBpm,
              useCrossfadeStore.getState().crossfade,
            )
            .then(() => {
              clearTimeout(timeoutId)
              if (!loadingCleared) {
                loadingCleared = true
                setLoading(false)
              }
              setStatus('Playing')
            })
            .catch((err: Error) => {
              clearTimeout(timeoutId)
              clearLoadingOnce()
              showError(err.message)
              setStatus('Error')
            })
        }
        else if (actualPlaying && !editorInitiated) {
          // Audio is playing but from PublicTracks, don't reload
          clearLoadingWithDelay()
          setStatus('Compiled (playback from Public Loops)')
        }
        else {
          clearLoadingWithDelay()
        }
        return true
      }
      else {
        // Single channel mode - only compile channel A
        const program = compiler.compile(compileCodeA)
        updateProgramInfo(compileCodeA, '')
        setStatus('Compiled')
        editorErrorsARef.current = []
        errorRef.current = ''
        setError('')
        setErrorsA([])
        // Update numbers with params for sliders - save to tab
        const { updateTab, activeTabId: currentActiveTabId } = getTabs()
        updateTab(currentActiveTabId, {
          numbersWithParamsA: program.numbersWithParams || [],
          numbersWithParamsB: [],
          patternEventsA: program.patternEvents || [],
          patternEventsB: [],
          sequencesA: program.sequences || [],
          sequencesB: [],
          amplitudeInfosA: program.amplitudeInfos || [],
          amplitudeInfosB: [],
          waveformInfosA: program.waveformInfos || [],
          waveformInfosB: [],
          levelInfosA: program.levelInfos || [],
          levelInfosB: [],
          timelineInfosA: program.timelineInfos || [],
          timelineInfosB: [],
        })
        console.log('[App] Updated tab (single mode) with timelineInfosA:', program.timelineInfos)

        // Only reload if audio is playing AND we're in the editor (not from PublicTracks)
        // AND the active tab is the one that's playing
        const actualPlaying = dspscript.isPlaying()
        const editorInitiated = getEditorInitiatedPlayback()
        const isActiveTabPlaying = activeTabId === playingTabId
        if (shouldReload && actualPlaying && dspscript.getAudioContext() && editorInitiated && isActiveTabPlaying) {
          let loadingCleared = false
          const clearLoadingOnce = () => {
            if (!loadingCleared) {
              loadingCleared = true
              clearLoadingWithDelay()
            }
          }
          const timeoutId = setTimeout(() => {
            clearLoadingOnce()
            setStatus('Error')
            showError('Loading timeout')
          }, 10000)
          dspscript
            .loadProgram(compileCodeA)
            .then(() => {
              clearTimeout(timeoutId)
              if (!loadingCleared) {
                loadingCleared = true
                setLoading(false)
              }
              setStatus('Playing')
            })
            .catch((err: Error) => {
              clearTimeout(timeoutId)
              clearLoadingOnce()
              showError(err.message)
              setStatus('Error')
            })
        }
        else if (actualPlaying && !editorInitiated) {
          // Audio is playing but from PublicTracks, don't reload
          clearLoadingWithDelay()
          setStatus('Compiled (playback from Public Loops)')
        }
        else {
          clearLoadingWithDelay()
        }
        return true
      }
    }
    catch (err: any) {
      clearLoadingWithDelay()
      console.error(err)
      // This catch should only handle single mode errors now
      // Dual mode errors are caught separately above
      const editorErrors = convertToEditorErrors(err, compileCodeA)
      editorErrorsARef.current = editorErrors
      setErrorsA(editorErrors)
      const errorMsg = editorErrors[0]?.message || err.message
      errorRef.current = errorMsg
      showError(errorMsg)
      setStatus('Error')
      return false
    }
  }

  // Update ref for handleCompile
  handleCompileRef.current = handleCompile

  // Refs are updated directly when errors are set, no need to sync from state

  useEffect(() => {
    errorRef.current = error
  }, [error])

  // Store updateProgramInfo in ref to avoid dependency issues
  const updateProgramInfoRef = useRef(updateProgramInfo)
  updateProgramInfoRef.current = updateProgramInfo

  // Handle successful compilation from AutoCompileManager
  // Use ref to store getTabs so callback always has latest reference
  const getTabsRef = useRef(getTabs)
  getTabsRef.current = getTabs

  const handleAutoCompileSuccess = useCallback(
    (codeA: string, codeB: string, programA: BytecodeProgram, programB?: BytecodeProgram) => {
      // CompiledOutput component now manages its own compilation, so we don't need to update it here
      updateProgramInfoRef.current(codeA, codeB)
      lastCompiledCodeA.current = codeA
      lastCompiledCodeB.current = codeB
      // Update widgets synchronously in one pass with code compilation
      const currentTab = getTabsRef.current().tabs.find(t => t.id === activeTabIdRef.current)
      if (currentTab) {
        const numbersA = programA.numbersWithParams || []
        const numbersB = programB?.numbersWithParams || []
        const patternsA = programA.patternEvents || []
        const patternsB = programB?.patternEvents || []
        const sequencesA = programA.sequences || []
        const sequencesB = programB?.sequences || []
        const waveformsA = programA.amplitudeInfos || []
        const waveformsB = programB?.amplitudeInfos || []
        const waveformInfosA = programA.waveformInfos || []
        const waveformInfosB = programB?.waveformInfos || []
        const levelInfosA = programA.levelInfos || []
        const levelInfosB = programB?.levelInfos || []
        const timelineInfosA = programA.timelineInfos || []
        const timelineInfosB = programB?.timelineInfos || []

        // Check if widgets actually changed by comparing array lengths and references
        // Since arrays are new on each compilation, we compare lengths as a quick check
        // If lengths are the same and references are the same (unlikely), skip update
        const currentNumbersA = currentTab.numbersWithParamsA || []
        const currentNumbersB = currentTab.numbersWithParamsB || []
        const currentPatternsA = currentTab.patternEventsA || []
        const currentPatternsB = currentTab.patternEventsB || []
        const currentSequencesA = currentTab.sequencesA || []
        const currentSequencesB = currentTab.sequencesB || []
        const currentWaveformsA = currentTab.amplitudeInfosA || []
        const currentWaveformsB = currentTab.amplitudeInfosB || []
        const currentWaveformInfosA = currentTab.waveformInfosA || []
        const currentWaveformInfosB = currentTab.waveformInfosB || []
        const currentLevelInfosA = currentTab.levelInfosA || []
        const currentLevelInfosB = currentTab.levelInfosB || []
        const currentTimelineInfosA = currentTab.timelineInfosA || []
        const currentTimelineInfosB = currentTab.timelineInfosB || []

        const widgetsChanged = currentNumbersA.length !== numbersA.length
          || currentNumbersB.length !== numbersB.length
          || currentPatternsA.length !== patternsA.length
          || currentPatternsB.length !== patternsB.length
          || currentSequencesA.length !== sequencesA.length
          || currentSequencesB.length !== sequencesB.length
          || currentWaveformsA.length !== waveformsA.length
          || currentWaveformsB.length !== waveformsB.length
          || currentWaveformInfosA.length !== waveformInfosA.length
          || currentWaveformInfosB.length !== waveformInfosB.length
          || currentLevelInfosA.length !== levelInfosA.length
          || currentLevelInfosB.length !== levelInfosB.length
          || currentTimelineInfosA.length !== timelineInfosA.length
          || currentTimelineInfosB.length !== timelineInfosB.length
          || currentTab.numbersWithParamsA !== numbersA
          || currentTab.numbersWithParamsB !== numbersB
          || currentTab.patternEventsA !== patternsA
          || currentTab.patternEventsB !== patternsB
          || currentTab.sequencesA !== sequencesA
          || currentTab.sequencesB !== sequencesB
          || currentTab.amplitudeInfosA !== waveformsA
          || currentTab.amplitudeInfosB !== waveformsB
          || currentTab.waveformInfosA !== waveformInfosA
          || currentTab.waveformInfosB !== waveformInfosB
          || currentTab.levelInfosA !== levelInfosA
          || currentTab.levelInfosB !== levelInfosB
          || currentTab.timelineInfosA !== timelineInfosA
          || currentTab.timelineInfosB !== timelineInfosB

        if (widgetsChanged) {
          // Update widgets synchronously - no debounce
          const { updateTab } = getTabsRef.current()
          updateTab(activeTabIdRef.current, {
            numbersWithParamsA: numbersA,
            numbersWithParamsB: numbersB,
            patternEventsA: patternsA,
            patternEventsB: patternsB,
            sequencesA: sequencesA,
            sequencesB: sequencesB,
            amplitudeInfosA: waveformsA,
            amplitudeInfosB: waveformsB,
            waveformInfosA: waveformInfosA,
            waveformInfosB: waveformInfosB,
            levelInfosA: levelInfosA,
            levelInfosB: levelInfosB,
            timelineInfosA: timelineInfosA,
            timelineInfosB: timelineInfosB,
          })
        }
      }
    },
    [],
  )

  const handleBpmChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value)
    if (!isNaN(value) && value > 0) {
      // Update BPM in control buffer - in dual mode this is enough,
      // the worklet will detect the change and update executors without reloading
      setGlobalBpm(value)
      // In single channel mode, still need to reload program
      if (isPlaying && dspscript.getAudioContext() && channelMode !== 'dual') {
        dspscript
          .loadNewProgram(codeFileA.value)
          .then(() => setStatus('Playing'))
          .catch((err: Error) => {
            showError(err.message)
            setStatus('Error')
          })
      }
    }
  }

  const handleLatencyHintChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value)
    if (!isNaN(value) && value >= 0) {
      setLatencyHint(value)
    }
  }

  // Keep refs updated for stable callbacks
  const activeTabIdRef = useRef(activeTabId)
  const channelModeRef = useRef(channelMode)
  const latencyHintRef = useRef(latencyHint)
  const globalBpmRef = useRef(globalBpm)
  const playingTabIdRef = useRef(playingTabId)
  activeTabIdRef.current = activeTabId
  channelModeRef.current = channelMode
  latencyHintRef.current = latencyHint
  globalBpmRef.current = globalBpm
  playingTabIdRef.current = playingTabId

  const handlePlay = useCallback(async () => {
    try {
      setActiveSidebarTrackId(null)
      setSidebarPlayingTrackId(null)
      // Mark that playback was initiated from the editor
      setEditorInitiatedPlayback(true)
      // Store the playing tab ID so we can restore it when navigating back from PublicTracks
      setEditorPlayingTabId(activeTabIdRef.current)

      if (!dspscript.getAudioContext()) {
        await dspscript.init(latencyHintRef.current)
        setStatus('Initialized')
      }

      // Skip loading if the same tab is already playing
      const isSameTabPlaying = channelModeRef.current === 'single'
        && dspscript.isPlaying()
        && playingTabIdRef.current === activeTabIdRef.current

      if (!isSameTabPlaying) {
        if (channelModeRef.current === 'dual') {
          // Compile to extract sequences and other metadata before loading
          const programA = compiler.compile(codeFileARef.current.value)
          const programB = compiler.compile(codeFileBRef.current.value)
          const { updateTab, activeTabId: currentActiveTabId } = getTabs()
          updateTab(currentActiveTabId, {
            numbersWithParamsA: programA.numbersWithParams || [],
            numbersWithParamsB: programB.numbersWithParams || [],
            patternEventsA: programA.patternEvents || [],
            patternEventsB: programB.patternEvents || [],
            sequencesA: programA.sequences || [],
            sequencesB: programB.sequences || [],
            amplitudeInfosA: programA.amplitudeInfos || [],
            amplitudeInfosB: programB.amplitudeInfos || [],
            waveformInfosA: programA.waveformInfos || [],
            waveformInfosB: programB.waveformInfos || [],
            levelInfosA: programA.levelInfos || [],
            levelInfosB: programB.levelInfos || [],
          })
          await dspscript.loadNewDualProgram(
            codeFileARef.current.value,
            codeFileBRef.current.value,
            globalBpmRef.current,
            useCrossfadeStore.getState().crossfade,
          )
          updateProgramInfo(codeFileARef.current.value, codeFileBRef.current.value)
        }
        else {
          // Compile to extract sequences and other metadata before loading
          const program = compiler.compile(codeFileARef.current.value)
          const { updateTab, activeTabId: currentActiveTabId } = getTabs()
          updateTab(currentActiveTabId, {
            numbersWithParamsA: program.numbersWithParams || [],
            numbersWithParamsB: [],
            patternEventsA: program.patternEvents || [],
            patternEventsB: [],
            sequencesA: program.sequences || [],
            sequencesB: [],
            amplitudeInfosA: program.amplitudeInfos || [],
            amplitudeInfosB: [],
            waveformInfosA: program.waveformInfos || [],
            waveformInfosB: [],
          })
          await dspscript.loadNewProgram(codeFileARef.current.value)
          updateProgramInfo(codeFileARef.current.value, '')
        }
      }

      // Set playingTabId BEFORE starting audio to avoid race condition
      // where animations check playingTabId before it's set
      if (channelModeRef.current === 'dual') {
        switchingFromDualRef.current = false
        setPlayingTabId(null)
      }
      else {
        switchingFromDualRef.current = false
        setPlayingTabId(activeTabIdRef.current)
      }

      await dspscript.start()

      setIsPlaying(true)
      setStatus('Playing')
      setError('')
    }
    catch (err: any) {
      showError(err.message)
      setStatus('Error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleStop = useCallback(async () => {
    try {
      autoDJStopRef.current = true
      await dspscript.stop()
      setIsPlaying(false)
      setPlayingTabId(null)
      setStatus('Stopped')
      // Clear the editor-initiated flag when stopping
      removeEditorInitiatedPlayback()
      removeEditorPlayingTabId()
    }
    catch (err: any) {
      showError((err as Error).message)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChannelModeToggle = useCallback(() => {
    if (channelModeRef.current === 'dual') {
      autoDJStopRef.current = true
    }
    getChannelModeStore().setChannelMode(channelModeRef.current === 'single' ? 'dual' : 'single')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLoadDualAInEditor = () => {
    const code = codeFileA.value
    const title = dualModeTitleA || 'Untitled'
    const artist = dualModeArtistA || ''

    const newTab: EditorTab = {
      id: `tab-${Date.now()}`,
      title: title,
      artist: artist,
      codeA: code,
      codeB: code,
      isDirty: true,
    }

    addTab(newTab)
    setActiveTabId(newTab.id)
    setChannelMode('single')

    prevSourceCodeA.current = code
    prevSourceCodeB.current = code
    lastCompiledCodeA.current = code
    lastCompiledCodeB.current = code

    requestAnimationFrame(() => {
      scrollTabIntoView(newTab.id)
    })

    setTimeout(async () => {
      try {
        const programA = compiler.compile(code)
        updateProgramInfo(code, '')
        setStatus(`Loaded: ${title}`)
        // Track compiled successfully - let AutoCompileManager handle error clearing
        setError('')
      }
      catch (err: any) {
        console.error(err)
        const editorErrors = convertToEditorErrors(err, code)
        setErrorsA(editorErrors)
        showError(editorErrors[0]?.message || err.message)
        setStatus('Error')
      }
    }, 100)
  }

  const handleLoadDualBInEditor = () => {
    const code = codeFileB.value
    const title = dualModeTitleB || 'Untitled'
    const artist = dualModeArtistB || ''

    const newTab: EditorTab = {
      id: `tab-${Date.now()}`,
      title: title,
      artist: artist,
      codeA: code,
      codeB: code,
      isDirty: true,
    }

    addTab(newTab)
    setActiveTabId(newTab.id)
    setChannelMode('single')

    prevSourceCodeA.current = code
    prevSourceCodeB.current = code
    lastCompiledCodeA.current = code
    lastCompiledCodeB.current = code

    requestAnimationFrame(() => {
      scrollTabIntoView(newTab.id)
    })

    setTimeout(async () => {
      try {
        const programA = compiler.compile(code)
        updateProgramInfo(code, '')
        setStatus(`Loaded: ${title}`)
        // Track compiled successfully - let AutoCompileManager handle error clearing
        setError('')
      }
      catch (err: any) {
        console.error(err)
        const editorErrors = convertToEditorErrors(err, code)
        setErrorsA(editorErrors)
        showError(editorErrors[0]?.message || err.message)
        setStatus('Error')
      }
    }, 100)
  }

  const generateAITrack = async (
    message: string,
    temperature: number,
    topP: number,
    model: string,
  ): Promise<{ title: string; code: string }> => {
    const response = await fetch('/api/ai/generate-track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        message,
        temperature,
        topP,
        model,
      }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to generate track' }))
      throw new Error(error.error || 'Failed to generate track')
    }

    const data = await response.json()
    if (!data.code) {
      throw new Error('No code received from AI')
    }

    return { title: data.title || 'AI Generated', code: data.code }
  }

  const handleAIGenerate = async (
    mode: 'new' | 'modify' | 'similar',
    message?: string,
    temperature?: number,
    topP?: number,
    model?: string,
  ) => {
    setIsGenerating(true)
    setShowAIModal(false)

    try {
      let endpoint = '/api/ai/generate-track'
      let body: any = {}

      if (mode === 'new') {
        endpoint = '/api/ai/generate-track'
        body = {
          message: message || '',
          temperature: temperature ?? 0.3,
          topP: topP ?? 1.0,
          model: model || 'gpt-5-chat-latest',
        }
      }
      else if (mode === 'modify') {
        endpoint = '/api/ai/modify-track'
        body = {
          message: message || '',
          code: codeFileA.value,
          temperature: temperature ?? 0.3,
          topP: topP ?? 1.0,
          model: model || 'gpt-5-chat-latest',
        }
      }
      else if (mode === 'similar') {
        endpoint = '/api/ai/similar-track'
        body = {
          code: codeFileA.value,
          temperature: temperature ?? 0.3,
          topP: topP ?? 1.0,
          model: model || 'gpt-5-chat-latest',
        }
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to generate loop' }))
        alert(error.error || 'Failed to generate loop')
        return
      }

      const { title, code } = await response.json()
      if (!code) {
        alert('No code received from AI')
        return
      }

      if (mode === 'modify') {
        codeFileA.replaceValue(code)
        codeFileB.replaceValue(code)
        if (channelMode === 'dual') {
          setDualModeCodeA(code, codeFileA.getState())
          setDualModeCodeB(code, codeFileB.getState())
        }
        else {
          updateTab(activeTabId, {
            codeA: code,
            codeB: code,
            isDirty: true,
          })
        }
        prevSourceCodeA.current = code
        prevSourceCodeB.current = code
      }
      else {
        // Create a new tab for 'new' and 'similar' modes
        const newTab: EditorTab = {
          id: `tab-${Date.now()}`,
          title: title || 'AI Generated',
          artist: '',
          codeA: code,
          codeB: code,
          isDirty: true,
        }

        addTab(newTab)
        setActiveTabId(newTab.id)

        // Scroll the new tab into view
        requestAnimationFrame(() => {
          scrollTabIntoView(newTab.id)
        })

        // Ensure editor reflects the new tab content immediately without marking dirty
        prevSourceCodeA.current = code
        prevSourceCodeB.current = code
        codeFileA.value = code
        codeFileB.value = code
      }
    }
    catch (error) {
      console.error('Failed to generate AI track:', error)
      alert('Failed to generate loop')
    }
    finally {
      setIsGenerating(false)
    }
  }

  const handleStartDJingSimilar = () => {
    const currentCode = codeFileA.value
    if (!currentCode.trim()) return

    const currentTitle = activeTab?.title || dualModeTitleA || 'Untitled'
    const currentArtist = activeTab?.artist || dualModeArtistA || ''

    setChannelMode('dual')
    setDualModeCodeA(currentCode, codeFileA.getState(), currentTitle, currentArtist)
    setDualModeCodeB('', undefined, '', '')
    setShowAIModal(false)
    setShowAutoDJModal(true)
  }

  const handleStartDJing = () => {
    const currentCode = codeFileA.value
    if (!currentCode.trim()) return

    const currentTitle = activeTab?.title || dualModeTitleA || 'Untitled'
    const currentArtist = activeTab?.artist || dualModeArtistA || ''

    setChannelMode('dual')

    // Update dual mode CodeFile instances directly
    const dualFiles = getDualModeCodeFiles()
    dualFiles.a.value = currentCode
    dualFiles.b.value = ''
    prevSourceCodeA.current = currentCode
    prevSourceCodeB.current = ''

    // Update store with CodeFile states
    setDualModeCodeA(currentCode, dualFiles.a.getState(), currentTitle, currentArtist)
    setDualModeCodeB('', dualFiles.b.getState(), '', '')

    setShowAIModal(false)
    setShowAutoDJModal(true)
  }

  const generateSimilarTrack = async (
    code: string,
    temperature: number,
    topP: number,
    model: string,
  ): Promise<{ title: string; code: string }> => {
    const response = await fetch('/api/ai/similar-track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        code,
        temperature,
        topP,
        model,
      }),
    })

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: 'Failed to generate similar track' }))
      throw new Error(error.error || 'Failed to generate similar track')
    }

    const data = await response.json()
    if (!data.code) {
      throw new Error('No code received from AI')
    }

    return { title: data.title || 'AI Generated', code: data.code }
  }

  const generateSimilarTrackFromMultiple = async (
    codes: string[],
    temperature: number,
    topP: number,
    model: string,
  ): Promise<{ title: string; code: string }> => {
    const response = await fetch('/api/ai/similar-track-multiple', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        codes,
        temperature,
        topP,
        model,
      }),
    })

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: 'Failed to generate similar track from multiple sources' }))
      throw new Error(error.error || 'Failed to generate similar track from multiple sources')
    }

    const data = await response.json()
    if (!data.code) {
      throw new Error('No code received from AI')
    }

    return { title: data.title || 'AI Generated', code: data.code }
  }

  const handleAutoDJStart = async (
    message: string,
    secondsToSwap: number,
    mixDuration: number,
    temperature: number,
    topP: number,
    model: string,
  ) => {
    if (channelMode !== 'dual') {
      alert('Auto DJ is only available in dual mode')
      return
    }

    setShowAutoDJModal(false)
    setIsAutoDJRunning(true)
    autoDJStopRef.current = false

    try {
      if (!dspscript.getAudioContext()) {
        await dspscript.init(latencyHint)
      }

      const dualFiles = getDualModeCodeFiles()

      const crossfadeSmoothly = async (from: number, to: number, duration: number) => {
        const steps = 100
        const stepDuration = duration / steps
        const nudgeStep = 0.01
        let currentFrom = from
        let lastSetValue = from
        let userIntervened = false

        for (let i = 0; i < steps; i++) {
          if (autoDJStopRef.current) return

          // Check if user manually changed the crossfade
          const actualCrossfade = useCrossfadeStore.getState().crossfade

          // If the actual value differs from what we last set, user intervened
          if (Math.abs(actualCrossfade - lastSetValue) > 0.01 && i > 0) {
            // User moved the fader - switch to nudge mode
            userIntervened = true
            currentFrom = actualCrossfade
            lastSetValue = actualCrossfade
          }

          if (userIntervened) {
            // After user intervention, use discrete nudges
            const remainingDistance = to - currentFrom
            if (Math.abs(remainingDistance) < nudgeStep) {
              // Close enough, set to target
              setCrossfade(to)
              lastSetValue = to
              currentFrom = to
              break
            }
            else {
              // Nudge toward target
              const direction = remainingDistance > 0 ? 1 : -1
              const value = Math.max(0, Math.min(1, currentFrom + direction * nudgeStep))
              setCrossfade(value)
              lastSetValue = value
              currentFrom = value
            }
          }
          else {
            // Normal smooth interpolation
            const remainingDistance = to - currentFrom
            const remainingSteps = steps - i
            const stepValue = remainingDistance / remainingSteps
            const value = currentFrom + stepValue
            setCrossfade(value)
            lastSetValue = value
            currentFrom = value
          }

          await new Promise(resolve => setTimeout(resolve, stepDuration * 1000))
        }

        // If user intervened and we haven't reached target, continue nudging
        if (userIntervened && !autoDJStopRef.current) {
          let currentValue = useCrossfadeStore.getState().crossfade
          while (Math.abs(currentValue - to) >= nudgeStep && !autoDJStopRef.current) {
            const remainingDistance = to - currentValue
            const direction = remainingDistance > 0 ? 1 : -1
            const value = Math.max(0, Math.min(1, currentValue + direction * nudgeStep))
            setCrossfade(value)
            currentValue = value
            await new Promise(resolve => setTimeout(resolve, stepDuration * 1000))
          }
        }

        // Ensure we end at the target and wait for sync
        if (!autoDJStopRef.current) {
          setCrossfade(to)
          // Small delay to ensure store and buffer are synced
          await new Promise(resolve => setTimeout(resolve, 10))
        }
      }

      // Check if both tracks are empty - if so, generate two tracks in parallel and start crossfading
      const isEmpty = !dualFiles.a.value.trim() && !dualFiles.b.value.trim()
      if (isEmpty) {
        // Generate both tracks in parallel
        let trackA: { title: string; code: string } | null = null
        let trackB: { title: string; code: string } | null = null
        let retries = 0
        const maxRetries = 5

        while ((!trackA || !trackB) && retries < maxRetries && !autoDJStopRef.current) {
          try {
            // Generate both tracks in parallel
            const [resultA, resultB]: [
              { title: string; code: string },
              { title: string; code: string },
            ] = await Promise.all([
              trackA ? Promise.resolve(trackA) : generateAITrack(message, temperature, topP, model),
              trackB ? Promise.resolve(trackB) : generateAITrack(message, temperature, topP, model),
            ])

            if (autoDJStopRef.current) return

            trackA = resultA
            trackB = resultB

            // Update editors and dual mode store immediately so code is visible even if compilation fails
            dualFiles.a.value = trackA.code
            dualFiles.b.value = trackB.code
            setDualModeCodeA(trackA.code, dualFiles.a.getState(), trackA.title, 'GPT')
            setDualModeCodeB(trackB.code, dualFiles.b.getState(), trackB.title, 'GPT')
            lastCompiledCodeA.current = trackA.code
            lastCompiledCodeB.current = trackB.code

            // Compile both tracks to extract BPM
            const currentBpm = getGlobalBpmStore().globalBpm
            const programA = compiler.compile(trackA.code, currentBpm)
            const programB = compiler.compile(trackB.code, currentBpm)

            // Use the first track's BPM as global BPM
            setGlobalBpm(programA.bpm)
            const trackBpm = programA.bpm

            // Clear any previous errors
            setError('')
            setErrorsA([])
            setErrorsB([])

            // Start at A (crossfade = 0)
            setCrossfade(0)
            dspscript.setCrossfade(0)

            // Load and start immediately with track's BPM
            await dspscript.loadDualProgram(trackA.code, trackB.code, trackBpm, 0)
            await dspscript.start()
            setIsPlaying(true)
            setEditorInitiatedPlayback(true)

            updateProgramInfo(trackA.code, trackB.code)

            // Wait for the designated seconds to swap before crossfading
            await new Promise(resolve => setTimeout(resolve, secondsToSwap * 1000))

            if (autoDJStopRef.current) return

            // Start crossfading from A to B
            await crossfadeSmoothly(0, 1, mixDuration)
          }
          catch (error: any) {
            console.error(
              `[Auto DJ] Error loading initial tracks (attempt ${retries + 1}/${maxRetries}):`,
              error,
            )
            retries++
            if (retries >= maxRetries) {
              throw new Error(
                `Failed to load initial tracks after ${maxRetries} attempts: ${error.message}`,
              )
            }
            // Reset tracks on error so they're regenerated
            trackA = null
            trackB = null
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        }

        if (!trackA || !trackB || autoDJStopRef.current) return

        // Continue with normal auto DJ flow, starting from B (since we just crossfaded to B)
        let currentChannel: 'A' | 'B' = 'B'

        // Continue with normal loop
        while (!autoDJStopRef.current) {
          await new Promise(resolve => setTimeout(resolve, secondsToSwap * 1000))

          if (autoDJStopRef.current) break

          // Generate and load track with retry on compilation errors
          let code: string | null = null
          let title: string = 'AI Generated'
          let retries = 0
          const maxRetries = 5

          while (!code && retries < maxRetries && !autoDJStopRef.current) {
            try {
              const track = await generateAITrack(message, temperature, topP, model)

              if (autoDJStopRef.current) break

              if (currentChannel === 'A') {
                dualFiles.b.value = track.code
                setDualModeCodeB(track.code, dualFiles.b.getState(), track.title, 'GPT')
                lastCompiledCodeB.current = track.code
                const currentCrossfade = useCrossfadeStore.getState().crossfade
                const currentBpm = getGlobalBpmStore().globalBpm
                await dspscript.loadDualProgram(
                  dualFiles.a.value,
                  track.code,
                  currentBpm,
                  currentCrossfade,
                )
                setCrossfade(currentCrossfade)
                code = track.code
                title = track.title
                // Clear any previous errors
                setError('')
                setErrorsB([])
                await crossfadeSmoothly(currentCrossfade, 1, mixDuration)
                currentChannel = 'B'
              }
              else {
                dualFiles.a.value = track.code
                setDualModeCodeA(track.code, dualFiles.a.getState(), track.title, 'GPT')
                lastCompiledCodeA.current = track.code
                const currentCrossfade = useCrossfadeStore.getState().crossfade
                const currentBpm = getGlobalBpmStore().globalBpm
                await dspscript.loadDualProgram(
                  track.code,
                  dualFiles.b.value,
                  currentBpm,
                  currentCrossfade,
                )
                setCrossfade(currentCrossfade)
                code = track.code
                title = track.title
                // Clear any previous errors
                setError('')
                setErrorsA([])
                await crossfadeSmoothly(currentCrossfade, 0, mixDuration)
                currentChannel = 'A'
              }
            }
            catch (error: any) {
              console.error(
                `[Auto DJ] Error loading track (attempt ${retries + 1}/${maxRetries}):`,
                error,
              )
              retries++
              if (retries >= maxRetries) {
                console.error(
                  `[Auto DJ] Failed to load track after ${maxRetries} attempts, continuing...`,
                )
                break
              }
              await new Promise(resolve => setTimeout(resolve, 1000))
            }
          }

          if (!code) {
            console.warn('[Auto DJ] Skipping this cycle due to compilation errors')
            continue
          }
        }

        return
      }

      // Get current crossfade position
      const currentCrossfade = useCrossfadeStore.getState().crossfade
      // Determine which channel we're closer to (0 = A, 1 = B)
      // If crossfade < 0.5, we're closer to A, so move fully to A (0)
      // If crossfade >= 0.5, we're closer to B, so move fully to B (1)
      const targetCrossfade = currentCrossfade < 0.5 ? 0 : 1
      // Start with the channel we moved to, and load first track in opposite
      let currentChannel: 'A' | 'B' = targetCrossfade === 0 ? 'A' : 'B'

      // Move crossfade immediately (not smoothly) to the channel we're closer to
      setCrossfade(targetCrossfade)

      // Start audio immediately if not already playing
      if (!dspscript.isPlaying()) {
        if (!dspscript.getAudioContext()) {
          await dspscript.init(latencyHint)
        }
        // Ensure both channels have code when loading the initial program
        const codeAForLoad = dualFiles.a.value.trim()
          ? dualFiles.a.value
          : dualFiles.b.value
        const codeBForLoad = dualFiles.b.value.trim()
          ? dualFiles.b.value
          : dualFiles.a.value

        const currentBpm = getGlobalBpmStore().globalBpm
        await dspscript.loadDualProgram(
          codeAForLoad,
          codeBForLoad,
          currentBpm,
          targetCrossfade,
        )
        await dspscript.start()
        setIsPlaying(true)
        setEditorInitiatedPlayback(true)
      }

      // Generate and load first track in the OPPOSITE channel with retry on compilation errors
      let firstCode: string | null = null
      let firstTitle: string = 'AI Generated'
      let retries = 0
      const maxRetries = 5

      while (!firstCode && retries < maxRetries && !autoDJStopRef.current) {
        try {
          const track = await generateAITrack(message, temperature, topP, model)
          if (autoDJStopRef.current) return

          if (currentChannel === 'A') {
            // We're in A (crossfade at 0), load first track in B
            dualFiles.b.value = track.code
            setDualModeCodeB(track.code, dualFiles.b.getState(), track.title, 'GPT')
            lastCompiledCodeA.current = dualFiles.a.value
            lastCompiledCodeB.current = track.code
            const currentCrossfade = useCrossfadeStore.getState().crossfade
            const currentBpm = getGlobalBpmStore().globalBpm
            await dspscript.loadDualProgram(
              dualFiles.a.value,
              track.code,
              currentBpm,
              currentCrossfade,
            )
            // Ensure store and buffer are in sync by updating through dspscript
            dspscript.setCrossfade(currentCrossfade)
            firstCode = track.code
            firstTitle = track.title
            // Clear any previous errors
            setError('')
            setErrorsB([])
          }
          else {
            // We're in B (crossfade at 1), load first track in A
            dualFiles.a.value = track.code
            setDualModeCodeA(track.code, dualFiles.a.getState(), track.title, 'GPT')
            lastCompiledCodeA.current = track.code
            lastCompiledCodeB.current = dualFiles.b.value
            const currentCrossfade = useCrossfadeStore.getState().crossfade
            const currentBpm = getGlobalBpmStore().globalBpm
            await dspscript.loadDualProgram(
              track.code,
              dualFiles.b.value,
              currentBpm,
              currentCrossfade,
            )
            // Ensure store and buffer are in sync by updating through dspscript
            dspscript.setCrossfade(currentCrossfade)
            firstCode = track.code
            firstTitle = track.title
            // Clear any previous errors
            setError('')
            setErrorsA([])
          }
        }
        catch (error: any) {
          console.error(
            `[Auto DJ] Error loading first track (attempt ${retries + 1}/${maxRetries}):`,
            error,
          )
          retries++
          if (retries >= maxRetries) {
            throw new Error(
              `Failed to load first track after ${maxRetries} attempts: ${error.message}`,
            )
          }
          // Wait a bit before retrying
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }

      if (!firstCode || autoDJStopRef.current) return

      updateProgramInfo(dualFiles.a.value, dualFiles.b.value)

      // Start mixing immediately with the first track
      const initialCrossfade = useCrossfadeStore.getState().crossfade
      if (currentChannel === 'A') {
        await crossfadeSmoothly(initialCrossfade, 1, mixDuration)
        currentChannel = 'B'
      }
      else {
        await crossfadeSmoothly(initialCrossfade, 0, mixDuration)
        currentChannel = 'A'
      }

      while (!autoDJStopRef.current) {
        await new Promise(resolve => setTimeout(resolve, secondsToSwap * 1000))

        if (autoDJStopRef.current) break

        // Generate and load track with retry on compilation errors
        let code: string | null = null
        let title: string = 'AI Generated'
        let retries = 0
        const maxRetries = 5

        while (!code && retries < maxRetries && !autoDJStopRef.current) {
          try {
            const track = await generateAITrack(message, temperature, topP, model)

            if (autoDJStopRef.current) break

            if (currentChannel === 'A') {
              dualFiles.b.value = track.code
              setDualModeCodeB(track.code, dualFiles.b.getState(), track.title, 'GPT')
              lastCompiledCodeB.current = track.code
              const currentCrossfade = useCrossfadeStore.getState().crossfade
              const currentBpm = getGlobalBpmStore().globalBpm
              await dspscript.loadDualProgram(
                dualFiles.a.value,
                track.code,
                currentBpm,
                currentCrossfade,
              )
              // Ensure store and buffer are in sync
              setCrossfade(currentCrossfade)
              code = track.code
              title = track.title
              // Clear any previous errors
              setError('')
              setErrorsB([])
              await crossfadeSmoothly(currentCrossfade, 1, mixDuration)
              currentChannel = 'B'
            }
            else {
              dualFiles.a.value = track.code
              setDualModeCodeA(track.code, dualFiles.a.getState(), track.title, 'GPT')
              lastCompiledCodeA.current = track.code
              const currentCrossfade = useCrossfadeStore.getState().crossfade
              const currentBpm = getGlobalBpmStore().globalBpm
              await dspscript.loadDualProgram(
                track.code,
                dualFiles.b.value,
                currentBpm,
                currentCrossfade,
              )
              // Ensure store and buffer are in sync
              setCrossfade(currentCrossfade)
              code = track.code
              title = track.title
              // Clear any previous errors
              setError('')
              setErrorsA([])
              await crossfadeSmoothly(currentCrossfade, 0, mixDuration)
              currentChannel = 'A'
            }
          }
          catch (error: any) {
            console.error(
              `[Auto DJ] Error loading track (attempt ${retries + 1}/${maxRetries}):`,
              error,
            )
            retries++
            if (retries >= maxRetries) {
              console.error(
                `[Auto DJ] Failed to load track after ${maxRetries} attempts, continuing...`,
              )
              // Continue the loop instead of stopping auto DJ
              break
            }
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        }

        // If we couldn't load a track after max retries, skip this cycle and continue
        if (!code) {
          console.warn('[Auto DJ] Skipping this cycle due to compilation errors')
          continue
        }
      }
    }
    catch (error) {
      console.error('Auto DJ error:', error)
      alert(`Auto DJ error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
    finally {
      setIsAutoDJRunning(false)
      autoDJStopRef.current = false
    }
  }

  const handleAutoDJSimilarStart = async (
    secondsToSwap: number,
    mixDuration: number,
    temperature: number,
    topP: number,
    model: string,
  ) => {
    if (channelMode !== 'dual') {
      alert('Auto DJ Similar is only available in dual mode')
      return
    }

    setShowAutoDJModal(false)
    setIsAutoDJRunning(true)
    autoDJStopRef.current = false

    try {
      if (!dspscript.getAudioContext()) {
        await dspscript.init(latencyHint)
      }

      const dualFiles = getDualModeCodeFiles()

      // Check if both A and B have code
      if (!dualFiles.a.value.trim() || !dualFiles.b.value.trim()) {
        alert('Both channels A and B must have code to start DJing Similar')
        setIsAutoDJRunning(false)
        return
      }

      // Get current crossfade position
      const currentCrossfade = useCrossfadeStore.getState().crossfade
      const targetCrossfade = currentCrossfade < 0.5 ? 0 : 1
      let currentChannel: 'A' | 'B' = targetCrossfade === 0 ? 'A' : 'B'

      const crossfadeSmoothly = async (from: number, to: number, duration: number) => {
        const steps = 100
        const stepDuration = duration / steps
        let currentFrom = from
        let lastSetValue = from

        for (let i = 0; i < steps; i++) {
          if (autoDJStopRef.current) return

          const actualCrossfade = useCrossfadeStore.getState().crossfade

          if (Math.abs(actualCrossfade - lastSetValue) > 0.01 && i > 0) {
            currentFrom = actualCrossfade
            lastSetValue = actualCrossfade
          }

          const remainingDistance = to - currentFrom
          const remainingSteps = steps - i
          const stepValue = remainingDistance / remainingSteps
          const value = currentFrom + stepValue
          setCrossfade(value)
          lastSetValue = value
          currentFrom = value

          await new Promise(resolve => setTimeout(resolve, stepDuration * 1000))
        }

        if (!autoDJStopRef.current) {
          setCrossfade(to)
          await new Promise(resolve => setTimeout(resolve, 10))
        }
      }

      setCrossfade(targetCrossfade)

      if (!dspscript.isPlaying()) {
        if (!dspscript.getAudioContext()) {
          await dspscript.init(latencyHint)
        }
        const currentBpm = getGlobalBpmStore().globalBpm
        await dspscript.loadDualProgram(
          dualFiles.a.value,
          dualFiles.b.value,
          currentBpm,
          targetCrossfade,
        )
        await dspscript.start()
        setIsPlaying(true)
        setEditorInitiatedPlayback(true)
      }

      // Generate first track similar to both A and B sources
      let firstCode: string | null = null
      let firstTitle: string = 'AI Generated'
      let retries = 0
      const maxRetries = 5

      while (!firstCode && retries < maxRetries && !autoDJStopRef.current) {
        try {
          const track = await generateSimilarTrackFromMultiple(
            [dualFiles.a.value, dualFiles.b.value],
            temperature,
            topP,
            model,
          )
          if (autoDJStopRef.current) return

          if (currentChannel === 'A') {
            dualFiles.b.value = track.code
            setDualModeCodeB(track.code, dualFiles.b.getState(), track.title, 'GPT')
            lastCompiledCodeA.current = dualFiles.a.value
            lastCompiledCodeB.current = track.code
            const currentCrossfade = useCrossfadeStore.getState().crossfade
            const currentBpm = getGlobalBpmStore().globalBpm
            await dspscript.loadDualProgram(
              dualFiles.a.value,
              track.code,
              currentBpm,
              currentCrossfade,
            )
            dspscript.setCrossfade(currentCrossfade)
            firstCode = track.code
            firstTitle = track.title
            // Clear any previous errors
            setError('')
            setErrorsB([])
          }
          else {
            dualFiles.a.value = track.code
            setDualModeCodeA(track.code, dualFiles.a.getState(), track.title, 'GPT')
            lastCompiledCodeA.current = track.code
            lastCompiledCodeB.current = dualFiles.b.value
            const currentCrossfade = useCrossfadeStore.getState().crossfade
            const currentBpm = getGlobalBpmStore().globalBpm
            await dspscript.loadDualProgram(
              track.code,
              dualFiles.b.value,
              currentBpm,
              currentCrossfade,
            )
            dspscript.setCrossfade(currentCrossfade)
            firstCode = track.code
            firstTitle = track.title
            // Clear any previous errors
            setError('')
            setErrorsA([])
          }
        }
        catch (error: any) {
          console.error(
            `[Auto DJ Similar] Error loading first track (attempt ${retries + 1}/${maxRetries}):`,
            error,
          )
          retries++
          if (retries >= maxRetries) {
            throw new Error(
              `Failed to load first track after ${maxRetries} attempts: ${error.message}`,
            )
          }
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }

      if (!firstCode || autoDJStopRef.current) return

      updateProgramInfo(dualFiles.a.value, dualFiles.b.value)

      const initialCrossfade = useCrossfadeStore.getState().crossfade
      if (currentChannel === 'A') {
        await crossfadeSmoothly(initialCrossfade, 1, mixDuration)
        currentChannel = 'B'
      }
      else {
        await crossfadeSmoothly(initialCrossfade, 0, mixDuration)
        currentChannel = 'A'
      }

      while (!autoDJStopRef.current) {
        await new Promise(resolve => setTimeout(resolve, secondsToSwap * 1000))

        if (autoDJStopRef.current) break

        // Get the currently playing track code (the one we just crossfaded to)
        const currentlyPlayingCode = currentChannel === 'A' ? dualFiles.a.value : dualFiles.b.value

        let code: string | null = null
        let title: string = 'AI Generated'
        let retries = 0
        const maxRetries = 5

        while (!code && retries < maxRetries && !autoDJStopRef.current) {
          try {
            // Generate similar track to the currently playing one
            const track = await generateSimilarTrack(currentlyPlayingCode, temperature, topP, model)

            if (autoDJStopRef.current) break

            if (currentChannel === 'A') {
              dualFiles.b.value = track.code
              setDualModeCodeB(track.code, dualFiles.b.getState(), track.title, 'GPT')
              lastCompiledCodeB.current = track.code
              const currentCrossfade = useCrossfadeStore.getState().crossfade
              const currentBpm = getGlobalBpmStore().globalBpm
              await dspscript.loadDualProgram(
                dualFiles.a.value,
                track.code,
                currentBpm,
                currentCrossfade,
              )
              setCrossfade(currentCrossfade)
              code = track.code
              title = track.title
              // Clear any previous errors
              setError('')
              setErrorsB([])
              await crossfadeSmoothly(currentCrossfade, 1, mixDuration)
              currentChannel = 'B'
            }
            else {
              dualFiles.a.value = track.code
              setDualModeCodeA(track.code, dualFiles.a.getState(), track.title, 'GPT')
              lastCompiledCodeA.current = track.code
              const currentCrossfade = useCrossfadeStore.getState().crossfade
              const currentBpm = getGlobalBpmStore().globalBpm
              await dspscript.loadDualProgram(
                track.code,
                dualFiles.b.value,
                currentBpm,
                currentCrossfade,
              )
              setCrossfade(currentCrossfade)
              code = track.code
              title = track.title
              // Clear any previous errors
              setError('')
              setErrorsA([])
              await crossfadeSmoothly(currentCrossfade, 0, mixDuration)
              currentChannel = 'A'
            }
          }
          catch (error: any) {
            console.error(
              `[Auto DJ Similar] Error loading track (attempt ${retries + 1}/${maxRetries}):`,
              error,
            )
            retries++
            if (retries >= maxRetries) {
              console.error(
                `[Auto DJ Similar] Failed to load track after ${maxRetries} attempts, continuing...`,
              )
              break
            }
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        }

        if (!code) {
          console.warn('[Auto DJ Similar] Skipping this cycle due to compilation errors')
          continue
        }
      }
    }
    catch (error) {
      console.error('Auto DJ Similar error:', error)
      alert(`Auto DJ Similar error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
    finally {
      setIsAutoDJRunning(false)
      autoDJStopRef.current = false
    }
  }

  const handleAutoDJSimilarA = async (
    secondsToSwap: number,
    mixDuration: number,
    temperature: number,
    topP: number,
    model: string,
  ) => {
    if (channelMode !== 'dual') {
      alert('Auto DJ Similar is only available in dual mode')
      return
    }

    const dualFiles = getDualModeCodeFiles()
    if (!dualFiles.a.value.trim()) {
      alert('Track A must have code to start DJing Similar to A')
      return
    }

    if (dualFiles.b.value.trim()) {
      alert('Track B must be empty to start DJing Similar to A')
      return
    }

    setShowAutoDJModal(false)
    setIsAutoDJRunning(true)
    autoDJStopRef.current = false

    try {
      if (!dspscript.getAudioContext()) {
        await dspscript.init(latencyHint)
      }

      // Move crossfader all the way to A (0) and start playing immediately
      setCrossfade(0)
      dspscript.setCrossfade(0)

      // Start playing immediately with existing track A
      if (!dspscript.isPlaying()) {
        if (!dspscript.getAudioContext()) {
          await dspscript.init(latencyHint)
        }
        const currentBpm = getGlobalBpmStore().globalBpm
        // Load with track A (B will be empty for now, filled when generated)
        await dspscript.loadDualProgram(
          dualFiles.a.value,
          dualFiles.b.value || dualFiles.a.value,
          currentBpm,
          0,
        )
        await dspscript.start()
        setIsPlaying(true)
        setEditorInitiatedPlayback(true)
      }
      else {
        // If already playing, ensure crossfade is at 0 and reload
        const currentBpm = getGlobalBpmStore().globalBpm
        await dspscript.loadDualProgram(
          dualFiles.a.value,
          dualFiles.b.value || dualFiles.a.value,
          currentBpm,
          0,
        )
        dspscript.setCrossfade(0)
      }

      // Generate similar track to A in the background
      let track: { title: string; code: string } | null = null
      let retries = 0
      const maxRetries = 5

      while (!track && retries < maxRetries && !autoDJStopRef.current) {
        try {
          track = await generateSimilarTrack(dualFiles.a.value, temperature, topP, model)
          if (autoDJStopRef.current) return

          // Fill B with the generated track
          dualFiles.b.value = track.code
          setDualModeCodeB(track.code, dualFiles.b.getState(), track.title, 'GPT')
          lastCompiledCodeB.current = track.code

          // Load the generated track into B
          const currentCrossfade = useCrossfadeStore.getState().crossfade
          const currentBpm = getGlobalBpmStore().globalBpm
          await dspscript.loadDualProgram(
            dualFiles.a.value,
            track.code,
            currentBpm,
            currentCrossfade,
          )
          dspscript.setCrossfade(currentCrossfade)

          setError('')
          setErrorsB([])
        }
        catch (error: any) {
          console.error(
            `[Auto DJ Similar A] Error generating track (attempt ${retries + 1}/${maxRetries}):`,
            error,
          )
          retries++
          if (retries >= maxRetries) {
            throw new Error(
              `Failed to generate track after ${maxRetries} attempts: ${error.message}`,
            )
          }
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }

      if (!track || autoDJStopRef.current) return

      updateProgramInfo(dualFiles.a.value, dualFiles.b.value)

      // Now continue with the similar track flow (similar to handleAutoDJSimilarStart)
      const crossfadeSmoothly = async (from: number, to: number, duration: number) => {
        const steps = 100
        const stepDuration = duration / steps
        let currentFrom = from
        let lastSetValue = from

        for (let i = 0; i < steps; i++) {
          if (autoDJStopRef.current) return

          const actualCrossfade = useCrossfadeStore.getState().crossfade

          if (Math.abs(actualCrossfade - lastSetValue) > 0.01 && i > 0) {
            currentFrom = actualCrossfade
            lastSetValue = actualCrossfade
          }

          const remainingDistance = to - currentFrom
          const remainingSteps = steps - i
          const stepValue = remainingDistance / remainingSteps
          const value = currentFrom + stepValue
          setCrossfade(value)
          lastSetValue = value
          currentFrom = value

          await new Promise(resolve => setTimeout(resolve, stepDuration * 1000))
        }

        if (!autoDJStopRef.current) {
          setCrossfade(to)
          await new Promise(resolve => setTimeout(resolve, 10))
        }
      }

      let currentChannel: 'A' | 'B' = 'A'

      // Start crossfading to B
      await crossfadeSmoothly(0, 1, mixDuration)
      currentChannel = 'B'

      // Continue the loop using generateSimilarTrackFromMultiple
      while (!autoDJStopRef.current) {
        await new Promise(resolve => setTimeout(resolve, secondsToSwap * 1000))

        if (autoDJStopRef.current) break

        let code: string | null = null
        let retries = 0
        const maxRetries = 5

        while (!code && retries < maxRetries && !autoDJStopRef.current) {
          try {
            // Generate similar track from both A and B
            const generatedTrack = await generateSimilarTrackFromMultiple(
              [dualFiles.a.value, dualFiles.b.value],
              temperature,
              topP,
              model,
            )

            if (autoDJStopRef.current) break

            if (currentChannel === 'A') {
              dualFiles.b.value = generatedTrack.code
              setDualModeCodeB(generatedTrack.code, dualFiles.b.getState(), generatedTrack.title, 'GPT')
              lastCompiledCodeB.current = generatedTrack.code
              const currentCrossfade = useCrossfadeStore.getState().crossfade
              const currentBpm = getGlobalBpmStore().globalBpm
              await dspscript.loadDualProgram(
                dualFiles.a.value,
                generatedTrack.code,
                currentBpm,
                currentCrossfade,
              )
              setCrossfade(currentCrossfade)
              code = generatedTrack.code
              setError('')
              setErrorsB([])
              await crossfadeSmoothly(currentCrossfade, 1, mixDuration)
              currentChannel = 'B'
            }
            else {
              dualFiles.a.value = generatedTrack.code
              setDualModeCodeA(generatedTrack.code, dualFiles.a.getState(), generatedTrack.title, 'GPT')
              lastCompiledCodeA.current = generatedTrack.code
              const currentCrossfade = useCrossfadeStore.getState().crossfade
              const currentBpm = getGlobalBpmStore().globalBpm
              await dspscript.loadDualProgram(
                generatedTrack.code,
                dualFiles.b.value,
                currentBpm,
                currentCrossfade,
              )
              setCrossfade(currentCrossfade)
              code = generatedTrack.code
              setError('')
              setErrorsA([])
              await crossfadeSmoothly(currentCrossfade, 0, mixDuration)
              currentChannel = 'A'
            }
          }
          catch (error: any) {
            console.error(
              `[Auto DJ Similar A] Error loading track (attempt ${retries + 1}/${maxRetries}):`,
              error,
            )
            retries++
            if (retries >= maxRetries) {
              console.error(
                `[Auto DJ Similar A] Failed to load track after ${maxRetries} attempts, continuing...`,
              )
              break
            }
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        }

        if (!code) {
          console.warn('[Auto DJ Similar A] Skipping this cycle due to compilation errors')
          continue
        }
      }
    }
    catch (error) {
      console.error('Auto DJ Similar A error:', error)
      alert(`Auto DJ Similar A error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
    finally {
      setIsAutoDJRunning(false)
      autoDJStopRef.current = false
    }
  }

  const handleAutoDJSimilarB = async (
    secondsToSwap: number,
    mixDuration: number,
    temperature: number,
    topP: number,
    model: string,
  ) => {
    if (channelMode !== 'dual') {
      alert('Auto DJ Similar is only available in dual mode')
      return
    }

    const dualFiles = getDualModeCodeFiles()
    if (!dualFiles.b.value.trim()) {
      alert('Track B must have code to start DJing Similar to B')
      return
    }

    if (dualFiles.a.value.trim()) {
      alert('Track A must be empty to start DJing Similar to B')
      return
    }

    setShowAutoDJModal(false)
    setIsAutoDJRunning(true)
    autoDJStopRef.current = false

    try {
      if (!dspscript.getAudioContext()) {
        await dspscript.init(latencyHint)
      }

      // Move crossfader all the way to B (1) and start playing immediately
      setCrossfade(1)
      dspscript.setCrossfade(1)

      // Start playing immediately with existing track B
      if (!dspscript.isPlaying()) {
        if (!dspscript.getAudioContext()) {
          await dspscript.init(latencyHint)
        }
        const currentBpm = getGlobalBpmStore().globalBpm
        // Load with track B (A will be empty for now, filled when generated)
        await dspscript.loadDualProgram(
          dualFiles.a.value || dualFiles.b.value,
          dualFiles.b.value,
          currentBpm,
          1,
        )
        await dspscript.start()
        setIsPlaying(true)
        setEditorInitiatedPlayback(true)
      }
      else {
        // If already playing, ensure crossfade is at 1 and reload
        const currentBpm = getGlobalBpmStore().globalBpm
        await dspscript.loadDualProgram(
          dualFiles.a.value || dualFiles.b.value,
          dualFiles.b.value,
          currentBpm,
          1,
        )
        dspscript.setCrossfade(1)
      }

      // Generate similar track to B in the background
      let track: { title: string; code: string } | null = null
      let retries = 0
      const maxRetries = 5

      while (!track && retries < maxRetries && !autoDJStopRef.current) {
        try {
          track = await generateSimilarTrack(dualFiles.b.value, temperature, topP, model)
          if (autoDJStopRef.current) return

          // Fill A with the generated track
          dualFiles.a.value = track.code
          setDualModeCodeA(track.code, dualFiles.a.getState(), track.title, 'GPT')
          lastCompiledCodeA.current = track.code

          // Load the generated track into A
          const currentCrossfade = useCrossfadeStore.getState().crossfade
          const currentBpm = getGlobalBpmStore().globalBpm
          await dspscript.loadDualProgram(
            track.code,
            dualFiles.b.value,
            currentBpm,
            currentCrossfade,
          )
          dspscript.setCrossfade(currentCrossfade)

          setError('')
          setErrorsA([])
        }
        catch (error: any) {
          console.error(
            `[Auto DJ Similar B] Error generating track (attempt ${retries + 1}/${maxRetries}):`,
            error,
          )
          retries++
          if (retries >= maxRetries) {
            throw new Error(
              `Failed to generate track after ${maxRetries} attempts: ${error.message}`,
            )
          }
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }

      if (!track || autoDJStopRef.current) return

      updateProgramInfo(dualFiles.a.value, dualFiles.b.value)

      // Now continue with the similar track flow
      const crossfadeSmoothly = async (from: number, to: number, duration: number) => {
        const steps = 100
        const stepDuration = duration / steps
        let currentFrom = from
        let lastSetValue = from

        for (let i = 0; i < steps; i++) {
          if (autoDJStopRef.current) return

          const actualCrossfade = useCrossfadeStore.getState().crossfade

          if (Math.abs(actualCrossfade - lastSetValue) > 0.01 && i > 0) {
            currentFrom = actualCrossfade
            lastSetValue = actualCrossfade
          }

          const remainingDistance = to - currentFrom
          const remainingSteps = steps - i
          const stepValue = remainingDistance / remainingSteps
          const value = currentFrom + stepValue
          setCrossfade(value)
          lastSetValue = value
          currentFrom = value

          await new Promise(resolve => setTimeout(resolve, stepDuration * 1000))
        }

        if (!autoDJStopRef.current) {
          setCrossfade(to)
          await new Promise(resolve => setTimeout(resolve, 10))
        }
      }

      let currentChannel: 'A' | 'B' = 'B'

      // Start crossfading to A
      await crossfadeSmoothly(1, 0, mixDuration)
      currentChannel = 'A'

      // Continue the loop using generateSimilarTrackFromMultiple
      while (!autoDJStopRef.current) {
        await new Promise(resolve => setTimeout(resolve, secondsToSwap * 1000))

        if (autoDJStopRef.current) break

        let code: string | null = null
        let retries = 0
        const maxRetries = 5

        while (!code && retries < maxRetries && !autoDJStopRef.current) {
          try {
            // Generate similar track from both A and B
            const generatedTrack = await generateSimilarTrackFromMultiple(
              [dualFiles.a.value, dualFiles.b.value],
              temperature,
              topP,
              model,
            )

            if (autoDJStopRef.current) break

            if (currentChannel === 'A') {
              dualFiles.b.value = generatedTrack.code
              setDualModeCodeB(generatedTrack.code, dualFiles.b.getState(), generatedTrack.title, 'GPT')
              lastCompiledCodeB.current = generatedTrack.code
              const currentCrossfade = useCrossfadeStore.getState().crossfade
              const currentBpm = getGlobalBpmStore().globalBpm
              await dspscript.loadDualProgram(
                dualFiles.a.value,
                generatedTrack.code,
                currentBpm,
                currentCrossfade,
              )
              setCrossfade(currentCrossfade)
              code = generatedTrack.code
              setError('')
              setErrorsB([])
              await crossfadeSmoothly(currentCrossfade, 1, mixDuration)
              currentChannel = 'B'
            }
            else {
              dualFiles.a.value = generatedTrack.code
              setDualModeCodeA(generatedTrack.code, dualFiles.a.getState(), generatedTrack.title, 'GPT')
              lastCompiledCodeA.current = generatedTrack.code
              const currentCrossfade = useCrossfadeStore.getState().crossfade
              const currentBpm = getGlobalBpmStore().globalBpm
              await dspscript.loadDualProgram(
                generatedTrack.code,
                dualFiles.b.value,
                currentBpm,
                currentCrossfade,
              )
              setCrossfade(currentCrossfade)
              code = generatedTrack.code
              setError('')
              setErrorsA([])
              await crossfadeSmoothly(currentCrossfade, 0, mixDuration)
              currentChannel = 'A'
            }
          }
          catch (error: any) {
            console.error(
              `[Auto DJ Similar B] Error loading track (attempt ${retries + 1}/${maxRetries}):`,
              error,
            )
            retries++
            if (retries >= maxRetries) {
              console.error(
                `[Auto DJ Similar B] Failed to load track after ${maxRetries} attempts, continuing...`,
              )
              break
            }
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        }

        if (!code) {
          console.warn('[Auto DJ Similar B] Skipping this cycle due to compilation errors')
          continue
        }
      }
    }
    catch (error) {
      console.error('Auto DJ Similar B error:', error)
      alert(`Auto DJ Similar B error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
    finally {
      setIsAutoDJRunning(false)
      autoDJStopRef.current = false
    }
  }

  const handleDownloadAudio = async (bars: number, oversampling: 1 | 2 | 4 | 8) => {
    try {
      setIsExporting(true)
      setError('')
      clearExportedAudio()

      const code = channelMode === 'single' ? codeFileA.value : codeFileA.value
      const { url, filename } = await exportAudioFromCode({
        code,
        globalBpm,
        bars,
        oversampling,
        title: activeTab?.title,
        artist: activeTab?.artist,
      })

      setExportedAudio(url, filename)
      setStatus('Audio exported!')
    }
    catch (err: any) {
      console.error('Download error:', err)
      setError(err.message || 'Failed to download audio')
      setStatus('Download failed')
    }
    finally {
      setIsExporting(false)
    }
  }

  const handleKeyOverride: KeyOverrideFunction = useCallback((event) => {
    if (event.altKey && (event.key === 'o' || event.key === 'O')) {
      event.preventDefault()
      const { wordWrap: currentWordWrap, setWordWrap } = useEditorSettingsStore.getState()
      setWordWrap(!currentWordWrap)
      return false
    }
    if (event.ctrlKey && event.key === 'r') return false
    if (event.ctrlKey && event.key === 'Enter') {
      event.preventDefault()
      // Defer compilation to allow React to render the loading spinner
      // Only reload if the active tab is the one playing, otherwise just compile
      const actualPlaying = dspscript.isPlaying()
      const isActiveTabPlaying = channelMode === 'single' ? activeTabId === playingTabId : actualPlaying
      const shouldReload = actualPlaying && isActiveTabPlaying
      setTimeout(() => {
        const compileFn = handleCompileRef.current
        if (compileFn) {
          compileFn(undefined, undefined, shouldReload)
        }
      }, 0)
      return false
    }
    return true
  }, [channelMode, activeTabId, playingTabId])

  // Global keyboard shortcuts for Alt+f and Alt+o
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (!e.altKey) return

      const target = e.target as HTMLElement
      const activeElement = document.activeElement as HTMLElement

      // For Alt+f and Alt+o, allow them to work even from canvas (editor)
      // but skip them for actual input/textarea elements where user might be typing
      const isAltFOrO = e.key === 'f' || e.key === 'F' || e.key === 'o' || e.key === 'O'

      // Skip if the event is coming from an editable element
      // For Alt+f and Alt+o, only skip input/textarea, not canvas
      if (
        target.tagName === 'INPUT'
        || target.tagName === 'TEXTAREA'
        || (!isAltFOrO && target.tagName === 'CANVAS')
        || target.isContentEditable
        || activeElement?.tagName === 'INPUT'
        || activeElement?.tagName === 'TEXTAREA'
        || (!isAltFOrO && activeElement?.tagName === 'CANVAS')
        || activeElement?.isContentEditable
        || target.closest('input, textarea' + (!isAltFOrO ? ', canvas' : '') + ', [contenteditable="true"]')
        || activeElement?.closest('input, textarea' + (!isAltFOrO ? ', canvas' : '') + ', [contenteditable="true"]')
      ) {
        return
      }

      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault()
        const { hideFunctionSignatures, setHideFunctionSignatures } = useEditorSettingsStore.getState()
        setHideFunctionSignatures(!hideFunctionSignatures)
      }
      else if (e.key === 'o' || e.key === 'O') {
        e.preventDefault()
        const { wordWrap: currentWordWrap, setWordWrap } = useEditorSettingsStore.getState()
        setWordWrap(!currentWordWrap)
      }
    }

    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [])

  // Stable callback for crossfade subscription - doesn't depend on any closure variables
  const handleCrossfadeChange = useCallback(() => {
    const currentState = useCrossfadeStore.getState()
    const currentIsPlaying = usePlaybackStore.getState().isPlaying
    const currentChannelMode = useChannelModeStore.getState().channelMode
    if (currentIsPlaying && currentChannelMode === 'dual') {
      dspscript.setCrossfade(currentState.crossfade)
    }
  }, [])

  useEffect(() => {
    if (!isPlaying || channelMode !== 'dual') return

    const crossfade = useCrossfadeStore.getState().crossfade
    dspscript.setCrossfade(crossfade)

    const unsubscribe = useCrossfadeStore.subscribe(handleCrossfadeChange)
    return unsubscribe
  }, [isPlaying, channelMode, handleCrossfadeChange])

  useEffect(() => {
    if (isPlaying && channelMode === 'dual') {
      dspscript.setGlobalBpm(globalBpm)
    }
  }, [globalBpm, isPlaying, channelMode])

  // Auto-compile when switching modes (but don't reload if playing from PublicTracks)
  useEffect(() => {
    const timer = setTimeout(() => {
      handleCompile(undefined, undefined, false)
    }, 50)
    return () => clearTimeout(timer)
  }, [channelMode])

  // Update playingTabId when switching modes while playing
  useEffect(() => {
    if (isPlaying) {
      if (channelMode === 'dual') {
        // In dual mode, there are no tabs, so clear playingTabId
        setPlayingTabId(null)
        // Clear stored tab ID since we're in dual mode
        removeEditorPlayingTabId()
        // Mark that we're in dual mode
        switchingFromDualRef.current = true
      }
      else {
        // When switching to single mode from dual mode while playing,
        // don't show any tab as playing since the code is from dual mode
        // and doesn't correspond to any specific tab
        if (switchingFromDualRef.current) {
          setPlayingTabId(null)
          removeEditorPlayingTabId()
          // Reset the flag after handling the switch
          switchingFromDualRef.current = false
        }
      }
    }
  }, [channelMode, isPlaying])

  // Scroll active tab into view when switching from dual to single mode
  useEffect(() => {
    if (prevChannelModeForScrollRef.current === 'dual' && channelMode === 'single' && activeTabId) {
      // Wait for tab bar to be rendered - the tab bar is conditionally rendered only in single mode
      let attempts = 0
      const maxAttempts = 10

      const attemptScroll = () => {
        attempts++
        if (tabBarRef.current) {
          const tabElement = tabBarRef.current.querySelector(`[data-tab-id="${activeTabId}"]`) as HTMLElement
          if (tabElement) {
            scrollTabIntoView(activeTabId)
            return true
          }
        }

        // Retry if tab bar or tab element not found yet
        if (attempts < maxAttempts) {
          requestAnimationFrame(attemptScroll)
        }
        return false
      }

      // Start attempting to scroll
      requestAnimationFrame(attemptScroll)
    }
    prevChannelModeForScrollRef.current = channelMode
  }, [channelMode, activeTabId])

  // Memoize NavBar buttons to prevent NavBar re-renders
  const navBarButtons = useMemo(
    () => (
      <>
        <button
          title="Documentation"
          className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2"
          onClick={() => setShowDocsModal(true)}
        >
          <BookOpenTextIcon weight="regular" size={16} />
        </button>
        <Link
          title="Feedback"
          className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2"
          href="https://loopmaster.featurebase.app"
          target="_blank"
        >
          <MegaphoneIcon weight="regular" size={16} />
        </Link>
        <Link
          title="Discord"
          className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-[#4c6dee] to-[#2a4bcc] text-white hover:opacity-90 flex items-center gap-2"
          href="https://discord.gg/NSWaB9dRYh"
          target="_blank"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="#fff" width="20" height="20" viewBox="0 0 20 20">
            <path d="M16.074,4.361a14.243,14.243,0,0,0-3.61-1.134,10.61,10.61,0,0,0-.463.96,13.219,13.219,0,0,0-4,0,10.138,10.138,0,0,0-.468-.96A14.206,14.206,0,0,0,3.919,4.364,15.146,15.146,0,0,0,1.324,14.5a14.435,14.435,0,0,0,4.428,2.269A10.982,10.982,0,0,0,6.7,15.21a9.294,9.294,0,0,1-1.494-.727c.125-.093.248-.19.366-.289a10.212,10.212,0,0,0,8.854,0c.119.1.242.2.366.289a9.274,9.274,0,0,1-1.5.728,10.8,10.8,0,0,0,.948,1.562,14.419,14.419,0,0,0,4.431-2.27A15.128,15.128,0,0,0,16.074,4.361Zm-8.981,8.1a1.7,1.7,0,0,1-1.573-1.79A1.689,1.689,0,0,1,7.093,8.881a1.679,1.679,0,0,1,1.573,1.791A1.687,1.687,0,0,1,7.093,12.462Zm5.814,0a1.7,1.7,0,0,1-1.573-1.79,1.689,1.689,0,0,1,1.573-1.791,1.679,1.679,0,0,1,1.573,1.791A1.688,1.688,0,0,1,12.907,12.462Z" />
          </svg>
        </Link>
        <button
          title={channelMode === 'dual' ? 'AI Auto DJ' : 'Generate loop with AI'}
          disabled={isGenerating || isAutoDJRunning}
          onClick={() => {
            if (channelMode === 'dual') {
              setShowAutoDJModal(true)
            }
            else {
              setShowAIModal(true)
            }
          }}
          className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
        >
          {isGenerating || isAutoDJRunning
            ? <SpinnerIcon weight="regular" size={16} className="animate-spin" />
            : <SparkleIcon weight="regular" size={16} />}
        </button>
        <button
          className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-orange-400 to-red-600 text-white hover:opacity-90 flex items-center gap-2"
          onClick={handleChannelModeToggle}
        >
          <SlidersHorizontalIcon weight="regular" size={16} />
          <span className="hidden md:inline">{channelMode === 'single' ? 'Single' : 'Dual'}</span>
        </button>
        <Link
          href="/"
          className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-lime-400 to-green-600 text-white hover:opacity-90 no-underline flex items-center gap-2"
        >
          <GlobeIcon weight="regular" size={16} />
          <span className="hidden md:inline">Browse</span>
        </Link>
      </>
    ),
    [channelMode, isGenerating, isAutoDJRunning, handleChannelModeToggle],
  )

  const visualizerButton = (
    <button
      onClick={() => setVisualizerMode(((visualizerMode + 1) % 4) as 0 | 1 | 2 | 3)}
      className="px-2 h-7 rounded hover:bg-[#333] flex items-center justify-center text-white opacity-50 hover:opacity-100"
      title={`Switch visualizer (${['Spectrum', 'Scrolling', 'Waveform', 'Lissajous'][visualizerMode]})`}
    >
      <LineSegmentsIcon weight="regular" size={16} />
    </button>
  )

  return (
    <div className="w-[100dvw] h-[100dvh] overflow-hidden relative">
      {user?.needsPassword && <SetPasswordModal />}
      <OnboardingModal isOpen={showOnboardingModal} onClose={handleOnboardingClose} />
      <DocsModal isOpen={showDocsModal} onClose={() => setShowDocsModal(false)} />
      <AIGenerateModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onGenerate={handleAIGenerate}
        isGenerating={isGenerating}
        onStartDJing={handleStartDJing}
      />
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      <AIAutoDJModal
        isOpen={showAutoDJModal}
        onClose={() => setShowAutoDJModal(false)}
        onStart={handleAutoDJStart}
        onStartSimilar={handleAutoDJSimilarStart}
        onStartSimilarA={handleAutoDJSimilarA}
        onStartSimilarB={handleAutoDJSimilarB}
        isRunning={isAutoDJRunning}
        hasTracksAB={!!(dualModeCodeA.trim() && dualModeCodeB.trim())}
        hasTrackA={!!dualModeCodeA.trim()}
        hasTrackB={!!dualModeCodeB.trim()}
      />
      <DownloadAudioModal
        isOpen={showExportModal}
        onClose={() => {
          setShowExportModal(false)
        }}
        onExport={handleDownloadAudio}
        onDownload={() => {
          if (exportedAudioUrl) {
            const a = document.createElement('a')
            a.href = exportedAudioUrl
            a.download = exportedFilename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }
        }}
        bpm={exportBpm}
        isExporting={isExporting}
        exportedAudioUrl={exportedAudioUrl}
        exportedFilename={exportedFilename}
      />
      <NavBar onPlay={handlePlay} onStop={handleStop} rightSideButtons={navBarButtons} />

      <div className="flex" style={{ height: `calc(100dvh - ${navBarHeight}px)` }}>
        <div className="flex-1 flex flex-col min-w-0">
          {/* Tab Bar - only show in single mode */}
          {channelMode === 'single' && (
            <div className="relative shrink-0">
              <EditorErrorBanner channel="A" />
              <TabBar
                ref={tabBarRef}
                activeTabId={activeTabId}
                playingTabId={playingTabId}
                activeSidebarTrackId={activeSidebarTrackId}
                isPlaying={isPlaying}
                onSwitchTab={handleSwitchTab}
                onCloseTab={handleCloseTab}
                onNewTab={handleNewTab}
              />
            </div>
          )}

          {channelMode === 'dual'
            ? (
              <>
                {isFullscreen
                  && createPortal(
                    <>
                      <div className="fixed left-0 right-0 top-0 bottom-0 z-[9998] pointer-events-none">
                        <Visualizers isPlaying={isPlaying} mode={visualizerMode} />
                      </div>
                      <div className="fixed left-0 right-0 top-0 bottom-0 z-[9999] flex">
                        <div className="absolute right-5 bottom-12 w-[200px] h-4 z-[3]">
                          <Crossfader />
                        </div>
                        <div className="absolute z-10 flex items-center gap-2 top-2 right-5">
                          {visualizerButton}
                          <EditorControls
                            currentTrack={trackForEditorControls}
                            onLoadInA={handleLoadInA}
                            onLoadInB={handleLoadInB}
                            channelMode={channelMode}
                          />
                        </div>
                        <div className="flex-1 flex flex-col border-r border-[#444] relative">
                          <div
                            ref={editorAContainerRef}
                            className="flex-1 flex flex-col overflow-hidden min-h-0"
                          >
                            <EditorPanel
                              codeFile={codeFileA}
                              channel="A"
                              keyOverride={handleKeyOverride}
                              isFullscreen={isFullscreen}
                            />
                          </div>
                        </div>
                        <div className="flex-1 flex flex-col relative">
                          <div
                            ref={editorBContainerRef}
                            className="flex-1 flex flex-col overflow-hidden min-h-0"
                          >
                            <EditorPanel
                              codeFile={codeFileB}
                              channel="B"
                              keyOverride={handleKeyOverride}
                              isFullscreen={isFullscreen}
                            />
                          </div>
                        </div>
                      </div>
                    </>,
                    document.body,
                  )}
                <div className="flex flex-1 min-h-0 relative">
                  <div className="absolute z-10 flex items-center gap-2 top-12 right-5">
                    <EditorControls
                      currentTrack={trackForEditorControls}
                      onLoadInA={handleLoadInA}
                      onLoadInB={handleLoadInB}
                      channelMode={channelMode}
                    />
                  </div>
                  <div className="flex-1 flex flex-col border-r border-[#444] relative">
                    <div
                      className={`h-[40px] px-4 flex items-center justify-between gap-2 border-b border-[#444] shrink-0 relative ${
                        focusedDualEditor === 'A'
                          ? 'bg-gradient-to-b from-neutral-600 to-neutral-900 text-white'
                          : 'bg-gradient-to-b from-black to-neutral-800 text-[#888] hover:bg-[#252525]'
                      }`}
                    >
                      <EditorErrorBanner channel="A" />

                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-sm text-orange-500 font-extrabold border border-neutral-700 w-6 h-6 flex items-center justify-center">
                          A
                        </span>
                        <span className="text-sm">
                          {dualModeArtistA || dualModeTitleA
                            ? `${dualModeArtistA ? `${dualModeArtistA} - ` : ''}${dualModeTitleA || ''}`
                            : ''}
                        </span>
                      </div>

                      <button
                        onClick={handleLoadDualAInEditor}
                        className="hidden md:flex px-2 h-6 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 items-center text-xs"
                        title="Load in Editor"
                      >
                        <CaretLeftIcon weight="regular" size={12} />
                      </button>
                    </div>
                    <div
                      ref={editorAContainerRef}
                      className="flex-1 flex flex-col overflow-hidden min-h-0"
                    >
                      <EditorPanel
                        codeFile={codeFileA}
                        channel="A"
                        keyOverride={handleKeyOverride}
                        isFullscreen={isFullscreen}
                      />
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col relative">
                    <div
                      className={`h-[40px] px-4 flex items-center justify-between gap-2 border-b border-[#444] shrink-0 relative ${
                        focusedDualEditor === 'B'
                          ? 'bg-gradient-to-b from-neutral-600 to-neutral-900 text-white'
                          : 'bg-gradient-to-b from-black to-neutral-800 text-[#888] hover:bg-[#252525]'
                      }`}
                    >
                      <EditorErrorBanner channel="B" />
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-sm text-orange-500 font-extrabold border border-neutral-700 w-6 h-6 flex items-center justify-center">
                          B
                        </span>
                        <span className="text-sm">
                          {dualModeArtistB || dualModeTitleB
                            ? `${dualModeArtistB ? `${dualModeArtistB} - ` : ''}${dualModeTitleB || ''}`
                            : ''}
                        </span>
                      </div>
                      <button
                        onClick={handleLoadDualBInEditor}
                        className="hidden md:flex px-2 h-6 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 items-center text-xs"
                        title="Load in Editor"
                      >
                        <CaretLeftIcon weight="regular" size={12} />
                      </button>
                    </div>
                    <div
                      ref={editorBContainerRef}
                      className="flex-1 flex flex-col overflow-hidden min-h-0"
                    >
                      <EditorPanel
                        codeFile={codeFileB}
                        channel="B"
                        keyOverride={handleKeyOverride}
                        isFullscreen={isFullscreen}
                      />
                    </div>
                  </div>
                </div>
              </>
            )
            : (
              <>
                {isFullscreen
                  && createPortal(
                    <>
                      <div className="fixed left-0 right-0 top-0 bottom-0 z-[9998] pointer-events-none">
                        <Visualizers isPlaying={isPlaying} mode={visualizerMode} />
                      </div>
                      <div className="fixed left-0 right-0 top-0 bottom-0 z-[9999] flex flex-col">
                        <div className="absolute top-2 right-5 z-10 flex items-center gap-2">
                          {visualizerButton}
                          <EditorControls
                            currentTrack={trackForEditorControls}
                            onLoadInA={handleLoadInA}
                            onLoadInB={handleLoadInB}
                            channelMode={channelMode}
                          />
                        </div>
                        <EditorPanel
                          codeFile={codeFileA}
                          channel="A"
                          keyOverride={handleKeyOverride}
                          isFullscreen={isFullscreen}
                        />
                      </div>
                    </>,
                    document.body,
                  )}
                <div className="relative flex-1 flex flex-col min-h-0 bg-black">
                  <div className="absolute top-2 right-5 z-10 flex items-center gap-2">
                    <EditorControls
                      currentTrack={trackForEditorControls}
                      onLoadInA={handleLoadInA}
                      onLoadInB={handleLoadInB}
                      channelMode={channelMode}
                    />
                  </div>
                  <EditorPanel
                    codeFile={codeFileA}
                    channel="A"
                    keyOverride={handleKeyOverride}
                    isFullscreen={isFullscreen}
                  />
                </div>
              </>
            )}
          <AutoCompileManager
            codeFileA={codeFileA}
            codeFileB={codeFileB}
            activeTabId={activeTabId}
            playingTabId={playingTabId}
            onCompile={handleAutoCompileSuccess}
            onClearError={handleClearError}
            onShowError={showError}
            trackLoadingTimeoutRef={trackLoadingTimeoutRef}
            onCompilingChange={setLoading}
          />
          <TabUpdateManager
            codeFileA={codeFileA}
            codeFileB={codeFileB}
            activeTabId={activeTabId}
            channelMode={channelMode}
          />
          <DualModeCodeUpdateManager
            codeFileA={codeFileA}
            codeFileB={codeFileB}
            channelMode={channelMode}
          />
        </div>

        <div className="hidden md:flex w-[400px] bg-black border-l border-[#444] flex-col">
          {!saveMenuOpen && !isFullscreen && (
            <div className="h-[200px] shrink-0 border-b border-[#333]">
              <Visualizers isPlaying={isPlaying} />
            </div>
          )}

          {/* Tabs */}
          <div className="h-[40px] bg-black flex shrink-0">
            <>
              {!hasNoAccount && (
                <button
                  onClick={() => setSidebarTab('tracks')}
                  className={`flex-1 font-semibold text-sm flex items-center justify-center gap-2 ${
                    sidebarTab === 'tracks'
                      ? 'bg-black text-white'
                      : 'bg-gradient-to-b from-black to-neutral-800 text-[#888] hover:text-white'
                  }`}
                >
                  <WaveformIcon weight="regular" size={16} />
                  My Loops
                </button>
              )}
              {!hasNoAccount && (
                <button
                  onClick={() => setSidebarTab('liked')}
                  className={`flex-1 font-semibold text-sm flex items-center justify-center gap-2 ${
                    sidebarTab === 'liked'
                      ? 'bg-black text-white'
                      : 'bg-gradient-to-b from-black to-neutral-800 text-[#888] hover:text-white'
                  }`}
                >
                  <HeartIcon weight="regular" size={16} />
                  Liked
                </button>
              )}
              <button
                onClick={() => setSidebarTab('compiled')}
                className={`flex-1 font-semibold text-sm flex items-center justify-center gap-2 ${
                  sidebarTab === 'compiled'
                    ? 'bg-black text-white'
                    : 'bg-gradient-to-b from-black to-neutral-800 text-[#888] hover:text-white'
                }`}
              >
                <ArticleIcon weight="regular" size={16} />
                Compiled
              </button>
            </>
          </div>

          {/* Tab Content */}
          {sidebarTab === 'compiled'
            ? <CompiledOutput />
            : sidebarTab === 'liked'
            ? (
              <TracksList
                tracks={tracks}
                likedTracks={likedTracks}
                user={user}
                loadingTracks={loadingTracks}
                loadingLiked={loadingLiked}
                loadingMoreTracks={loadingMoreTracks}
                loadingMoreLiked={loadingMoreLiked}
                hasMoreTracks={hasMoreTracks}
                hasMoreLikedTracks={hasMoreLikedTracks}
                sidebarTab="liked"
                activeSidebarTrackId={activeSidebarTrackId}
                playingSidebarTrackId={sidebarPlayingTrackId}
                isPlaying={isPlaying}
                channelMode={channelMode}
                likedTrackIds={likedTrackIds}
                trackErrors={trackErrors}
                onPlaySidebarTrack={handlePlaySidebarTrack}
                onStopSidebarTrack={handleStopSidebarTrack}
                onLoadTrack={handleLoadTrack}
                onLoadInA={handleLoadInA}
                onLoadInB={handleLoadInB}
                onToggleLike={handleToggleLike}
                onShowAuthModal={() => setShowAuthModal(true)}
                onLoadMoreTracks={handleLoadMoreTracks}
                onLoadMoreLiked={handleLoadMoreLiked}
                hasNoAccount={hasNoAccount}
              />
            )
            : (
              <div className="flex-1 flex flex-col overflow-hidden bg-black">
                {/* Save Form */}
                <SaveForm
                  channelMode={channelMode}
                  user={user}
                  saveTitle={saveTitle}
                  effectiveSaveTitle={effectiveSaveTitle}
                  saveButtonState={saveButtonState}
                  saving={saving}
                  generatingTitle={generatingTitle}
                  isExporting={isExporting}
                  saveMenuOpen={saveMenuOpen}
                  savePublic={savePublic}
                  currentTrack={currentTrack}
                  currentTrackId={currentTrackId}
                  tracks={tracks}
                  likedTracks={likedTracks}
                  onSaveTitleChange={handleSaveTitleChange}
                  onSaveTitleKeyDown={handleSaveTitleKeyDown}
                  onToggleSaveMenu={handleToggleSaveMenu}
                  onSave={handleSave}
                  onSaveNew={handleSaveNewTrack}
                  onGenerateTitle={handleGenerateTitle}
                  onExport={handleExport}
                  onDownloadAllTracksZip={handleDownloadAllTracksZip}
                  onDeleteTrack={handleDeleteTrackCallback}
                  onVisibilityToggle={handleVisibilityToggle}
                  onSaveAsRemix={handleSaveAsRemix}
                  onShowAuthModal={() => setShowAuthModal(true)}
                  onSetExportBpm={setExportBpm}
                  onSetShowExportModal={setShowExportModal}
                  onSetError={setError}
                />

                {/* Tracks List */}
                <TracksList
                  tracks={tracks}
                  likedTracks={likedTracks}
                  user={user}
                  loadingTracks={loadingTracks}
                  loadingLiked={loadingLiked}
                  loadingMoreTracks={loadingMoreTracks}
                  loadingMoreLiked={loadingMoreLiked}
                  hasMoreTracks={hasMoreTracks}
                  hasMoreLikedTracks={hasMoreLikedTracks}
                  sidebarTab="tracks"
                  activeSidebarTrackId={activeSidebarTrackId}
                  playingSidebarTrackId={sidebarPlayingTrackId}
                  isPlaying={isPlaying}
                  channelMode={channelMode}
                  likedTrackIds={likedTrackIds}
                  trackErrors={trackErrors}
                  onPlaySidebarTrack={handlePlaySidebarTrack}
                  onStopSidebarTrack={handleStopSidebarTrack}
                  onLoadTrack={handleLoadTrack}
                  onLoadInA={handleLoadInA}
                  onLoadInB={handleLoadInB}
                  onToggleLike={handleToggleLike}
                  onShowAuthModal={() => setShowAuthModal(true)}
                  onLoadMoreTracks={handleLoadMoreTracks}
                  onLoadMoreLiked={handleLoadMoreLiked}
                  hasNoAccount={hasNoAccount}
                />
              </div>
            )}

          {channelMode === 'dual' && !isFullscreen && <Crossfader />}
        </div>
      </div>
    </div>
  )
}

export default App
